import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavigationContext } from '../../../context/navigation-context';
import useFetch from '../../../hooks/fetchMSSQL-hook';
import { NotificationManager } from 'react-notifications';
import VpnSqlPanel from './VpnSqlPanel';
import { API_URL } from '../../../config/config';

const emptyStatus = {
  headscale: { online: false, totalNodes: 0, onlineNodes: 0, nodes: [], error: null, disk: null },
  refreshedAt: null,
};

const getClientColor = (clientAbbr) => {
  if (!clientAbbr) return '#6c757d';
  const colors = [
    '#d93025',
    '#1a73e8',
    '#137333',
    '#8430c5',
    '#b06000',
    '#007b83',
    '#a9128f',
    '#0f9d58',
    '#e06000' 
  ];
  let hash = 0;
  const str = String(clientAbbr).toLowerCase().trim();
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const VPN = () => {
  const { setHeaderData } = useContext(NavigationContext);
  const { fetchMssqlApi } = useFetch();
  const refreshEveryMinutes = 15;

  const [statusData, setStatusData] = useState(emptyStatus);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [nodeSearch, setNodeSearch] = useState('');
  const [showOffline, setShowOffline] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeDiskData, setNodeDiskData] = useState(null);
  const [nodeSettingsData, setNodeSettingsData] = useState(null);
  const [nodeNetworkData, setNodeNetworkData] = useState(null);
  const [scriptOutputs, setScriptOutputs] = useState({});
  const [runningScripts, setRunningScripts] = useState({});
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [hasConnected, setHasConnected] = useState(false);
  const [choicesData, setChoicesData] = useState(null);
  const [isChoicesLoading, setIsChoicesLoading] = useState(false);
  const [errorsData, setErrorsData] = useState(null);
  const [isErrorsLoading, setIsErrorsLoading] = useState(false);
  const [errorsPage, setErrorsPage] = useState(1);
  const [filesEncodingData, setFilesEncodingData] = useState(null);
  const [isEncodingLoading, setIsEncodingLoading] = useState(false);
  const [trxData, setTrxData] = useState(null);
  const [isTrxLoading, setIsTrxLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configFilesList, setConfigFilesList] = useState([]);
  const [tempFilesList, setTempFilesList] = useState([]);
  const [newFilePathInput, setNewFilePathInput] = useState('');
  const [deleteFilesCheckResults, setDeleteFilesCheckResults] = useState(null);
  const [isDeleteFilesChecking, setIsDeleteFilesChecking] = useState(false);
  const [heartbeatCheckData, setHeartbeatCheckData] = useState(null);
  const [initPaymentCheckData, setInitPaymentCheckData] = useState(null);
  const [isInitPaymentChecking, setIsInitPaymentChecking] = useState(false);
  const [isInitPaymentFixing, setIsInitPaymentFixing] = useState(false);
  const [isHeartbeatChecking, setIsHeartbeatChecking] = useState(false);
  const [isHeartbeatFixing, setIsHeartbeatFixing] = useState(false);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUpdateVersionName, setSelectedUpdateVersionName] = useState('');
  const [updateVersionsList, setUpdateVersionsList] = useState([]);
  const [tempUpdateVersionsList, setTempUpdateVersionsList] = useState([]);
  const [activeConfigTab, setActiveConfigTab] = useState('delete-paths');
  const [storeFilesList, setStoreFilesList] = useState([]);

  const [editingVersionIndex, setEditingVersionIndex] = useState(null);
  const [verFormName, setVerFormName] = useState('');

  const [verFormSteps, setVerFormSteps] = useState([]);
  const [verFormRollbackSteps, setVerFormRollbackSteps] = useState([]);

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSteps, setUpdateSteps] = useState([]);
  const [updateLog, setUpdateLog] = useState('');
  const [updateError, setUpdateError] = useState(null);
  const [lastExecutedCommand, setLastExecutedCommand] = useState('');
  const [totalCommandsCount, setTotalCommandsCount] = useState(0);
  const [completedCommandsCount, setCompletedCommandsCount] = useState(0);

  const [showCustomCmdModal, setShowCustomCmdModal] = useState(false);
  const [customCommandsList, setCustomCommandsList] = useState(() => {
    try {
      const saved = localStorage.getItem('vpn_custom_commands_last');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
    return [
      { id: '1', command: '', showResponse: true }
    ];
  });
  const [customCmdStopOnError, setCustomCmdStopOnError] = useState(true);
  const [customCmdTimeout, setCustomCmdTimeout] = useState(60000);
  const [isCustomCmdRunning, setIsCustomCmdRunning] = useState(false);
  const [customCmdResults, setCustomCmdResults] = useState(null);
  const [customCmdError, setCustomCmdError] = useState(null);
  const [revealedResponses, setRevealedResponses] = useState({});
  const [customPresets, setCustomPresets] = useState(() => {
    try {
      const saved = localStorage.getItem('vpn_custom_presets');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (_) {}
    return [];
  });
  const [newPresetName, setNewPresetName] = useState('');
  const [showSavePresetInput, setShowSavePresetInput] = useState(false);

  const itemsPerPage = 30;

  const [timeTicker, setTimeTicker] = useState(Date.now());

  useEffect(() => {
    setHeaderData({ text: 'Headscale VPN' });
  }, [setHeaderData]);

  const fetchMssqlApiRef = useRef(fetchMssqlApi);
  useEffect(() => {
    fetchMssqlApiRef.current = fetchMssqlApi;
  }, [fetchMssqlApi]);

  useEffect(() => {
    fetchMssqlApiRef.current(
      'user/vpn-delete-paths',
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.paths) {
          setConfigFilesList(response.paths);
        }
      }
    );

    fetchMssqlApiRef.current(
      'user/vpn-update-versions',
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.versions) {
          setUpdateVersionsList(response.versions);
        }
      }
    );

    fetchMssqlApiRef.current(
      'user/vpn-update-store-files',
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.files) {
          setStoreFilesList(response.files);
        }
      }
    );
  }, []);

  const loadStatus = useCallback(() => {
    setIsLoading(true);
    setLoadError('');

    fetchMssqlApi(
      'user/server-status',
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        setStatusData({ ...emptyStatus, ...(response || {}) });
        setIsLoading(false);
      },
      () => {
        setLoadError('Nie udało się pobrać statusów sieci VPN.');
        setIsLoading(false);
      }
    );
  }, [fetchMssqlApi]);

  const loadStatusRef = useRef(loadStatus);
  useEffect(() => {
    loadStatusRef.current = loadStatus;
  }, [loadStatus]);

  useEffect(() => {
    loadStatusRef.current();

    const intervalId = window.setInterval(() => {
      loadStatusRef.current();
    }, refreshEveryMinutes * 60 * 1000);

    const tickerId = window.setInterval(() => {
      setTimeTicker(Date.now());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
      window.clearInterval(tickerId);
    };
  }, [refreshEveryMinutes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [nodeSearch, showOffline]);

  const handleNodeClick = (node) => {
    if (!node.online) return;

    setSelectedNode(node);
    setIsModalLoading(false);
    setHasConnected(false);
    setNodeDiskData(null);
    setNodeSettingsData(null);
    setNodeNetworkData(null);
    setScriptOutputs({});
    setRunningScripts({});
    setChoicesData(null);
    setErrorsData(null);
    setErrorsPage(1);
    setFilesEncodingData(null);
    setTrxData(null);
    setDeleteFilesCheckResults(null);
    setHeartbeatCheckData(null);
    setInitPaymentCheckData(null);
  };

  const handleConnect = () => {
    if (!selectedNode) return;
    setIsModalLoading(true);
    setHasConnected(true);
    setNodeDiskData(null);
    setNodeSettingsData(null);
    setNodeNetworkData(null);

    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      `user/server-status?nodeIp=${nodeIp}`,
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.headscale) {
          if (response.headscale.disk) {
            setNodeDiskData(response.headscale.disk);
          }
          if (response.headscale.settings) {
            setNodeSettingsData(response.headscale.settings);
          }
          if (response.headscale.network) {
            setNodeNetworkData(response.headscale.network);
          }
        }
        setIsModalLoading(false);
      },
      () => {
        setIsModalLoading(false);
      }
    );
  };

  const handleCheckChoices = () => {
    if (!selectedNode) return;
    setIsChoicesLoading(true);
    setChoicesData(null);

    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      `user/remote-choices?nodeIp=${nodeIp}`,
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.choices) {
          setChoicesData(response.choices);
        } else {
          NotificationManager.error('Nie znaleziono danych o wyborach.', 'Błąd');
        }
        setIsChoicesLoading(false);
      },
      () => {
        NotificationManager.error('Błąd połączenia z API podczas pobierania wyborów.', 'Błąd');
        setIsChoicesLoading(false);
      }
    );
  };

  const handleCheckErrors = () => {
    if (!selectedNode) return;
    setIsErrorsLoading(true);
    setErrorsData(null);
    setErrorsPage(1);

    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      `user/remote-errors?nodeIp=${nodeIp}`,
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.errors) {
          setErrorsData(response.errors);
        } else {
          NotificationManager.error('Nie znaleziono danych o błędach.', 'Błąd');
        }
        setIsErrorsLoading(false);
      },
      () => {
        NotificationManager.error('Błąd połączenia z API podczas pobierania błędów.', 'Błąd');
        setIsErrorsLoading(false);
      }
    );
  };

  const handleCheckEncoding = () => {
    if (!selectedNode) return;
    setIsEncodingLoading(true);
    setFilesEncodingData(null);

    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      `user/remote-file-encodings?nodeIp=${nodeIp}`,
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.files) {
          setFilesEncodingData(response.files);
        } else {
          NotificationManager.error('Nie znaleziono danych o kodowaniu plików.', 'Błąd');
        }
        setIsEncodingLoading(false);
      },
      () => {
        NotificationManager.error('Błąd połączenia z API podczas sprawdzania kodowania plików.', 'Błąd');
        setIsEncodingLoading(false);
      }
    );
  };

  const handleCheckTransactions = () => {
    if (!selectedNode) return;
    setIsTrxLoading(true);
    setTrxData(null);

    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      `user/remote-transactions?nodeIp=${nodeIp}`,
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response && response.transactions) {
          setTrxData(response.transactions);
        } else {
          NotificationManager.error('Nie znaleziono danych o transakcjach.', 'Błąd');
        }
        setIsTrxLoading(false);
      },
      () => {
        NotificationManager.error('Błąd połączenia z API podczas pobierania transakcji.', 'Błąd');
        setIsTrxLoading(false);
      }
    );
  };

  const handleCheckDeleteFiles = useCallback((node, overridePaths) => {
    const targetNode = node || selectedNode;
    if (!targetNode) return;
    const paths = overridePaths || configFilesList;
    if (paths.length === 0) {
      setDeleteFilesCheckResults([]);
      return;
    }

    setIsDeleteFilesChecking(true);
    const nodeIp = (targetNode.ipAddresses && targetNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      'user/remote-files-exist',
      {
        method: 'POST',
        data: { nodeIp, files: paths },
        hideNotification: true
      },
      (response) => {
        if (response && response.results) {
          setDeleteFilesCheckResults(response.results);
        } else {
          NotificationManager.error('Nie udało się sprawdzić istnienia plików.', 'Błąd');
        }
        setIsDeleteFilesChecking(false);
      },
      () => {
        NotificationManager.error('Błąd połączenia z API podczas sprawdzania plików.', 'Błąd');
        setIsDeleteFilesChecking(false);
      }
    );
  }, [selectedNode, fetchMssqlApi, configFilesList]);

  const handleInitPayment = (fix = false) => {
    if (!selectedNode) return;
    if (fix && !window.confirm('Podmienić init-payment.sh na nową wersję z pętlą oczekiwania na zakończenie płatności? Skrypt nie zostanie uruchomiony.')) return;
    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    const setLoading = fix ? setIsInitPaymentFixing : setIsInitPaymentChecking;
    setLoading(true);
    if (!fix) setInitPaymentCheckData(null);
    fetchMssqlApi(
      fix ? 'user/remote-init-payment-fix' : `user/remote-init-payment-check?nodeIp=${encodeURIComponent(nodeIp)}`,
      { method: fix ? 'POST' : 'GET', ...(fix ? { data: { nodeIp } } : {}), hideNotification: true, showLoader: false, isErrorAcceptable: true },
      response => {
        setLoading(false);
        if (response && (fix ? response.success : typeof response.isCurrent === 'boolean')) {
          setInitPaymentCheckData(fix ? { exists: true, isCurrent: true } : response);
          if (fix) NotificationManager.success('Zaktualizowano init-payment.sh.', 'Sukces', 3000);
        } else NotificationManager.error('Nie udało się sprawdzić lub zaktualizować init payment.', 'Błąd');
      },
      () => {
        setLoading(false);
        NotificationManager.error('Błąd połączenia z API podczas obsługi init payment.', 'Błąd');
      }
    );
  };

  const handleCheckHeartbeat = () => {
    if (!selectedNode) return;
    setIsHeartbeatChecking(true);
    setHeartbeatCheckData(null);

    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      `user/remote-heartbeat-check?nodeIp=${nodeIp}`,
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        if (response) {
          setHeartbeatCheckData(response);
        } else {
          NotificationManager.error('Nie udało się sprawdzić heartbeat.', 'Błąd');
        }
        setIsHeartbeatChecking(false);
      },
      () => {
        NotificationManager.error('Błąd połączenia z API podczas sprawdzania heartbeat.', 'Błąd');
        setIsHeartbeatChecking(false);
      }
    );
  };

  const handleFixHeartbeat = () => {
    if (!selectedNode) return;
    if (!window.confirm('Czy na pewno chcesz naprawić heartbeat? Stare pliki zostaną usunięte i pobrane nowe z portalu.')) return;

    setIsHeartbeatFixing(true);

    const nodeIp = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
    fetchMssqlApi(
      'user/remote-heartbeat-fix',
      {
        method: 'POST',
        data: { nodeIp },
        hideNotification: true
      },
      (response) => {
        if (response && response.success) {
          NotificationManager.success('Heartbeat został naprawiony pomyślnie.', 'Sukces', 3000);
          setHeartbeatCheckData({ isCurrent: true, output: 'Naprawiono - pobrano nowe pliki z portalu.' });
        } else {
          NotificationManager.error('Nie udało się naprawić heartbeat.', 'Błąd');
        }
        setIsHeartbeatFixing(false);
      },
      () => {
        NotificationManager.error('Błąd połączenia z API podczas naprawy heartbeat.', 'Błąd');
        setIsHeartbeatFixing(false);
      }
    );
  };

  const handleOpenConfigModal = () => {
    setNewFilePathInput('');
    setTempFilesList(configFilesList);
    setTempUpdateVersionsList(JSON.parse(JSON.stringify(updateVersionsList)));
    setActiveConfigTab('delete-paths');
    setEditingVersionIndex(null);
    setVerFormName('');

    setShowConfigModal(true);
  };

  const handleAddNewFilePath = () => {
    const trimmed = newFilePathInput.trim();
    if (!trimmed) return;
    if (tempFilesList.indexOf(trimmed) !== -1) {
      NotificationManager.warning('Ta ścieżka jest już na liście.', 'Uwaga', 2000);
      return;
    }
    setTempFilesList(function (prev) { return prev.concat(trimmed); });
    setNewFilePathInput('');
  };



  const handleAddStep = (type) => {
    if (type === 'step') {
      setVerFormSteps([...verFormSteps, { name: '', command: '' }]);
    } else {
      setVerFormRollbackSteps([...verFormRollbackSteps, { name: '', command: '' }]);
    }
  };

  const handleUpdateStep = (type, index, field, value) => {
    if (type === 'step') {
      const newSteps = [...verFormSteps];
      newSteps[index][field] = value;
      setVerFormSteps(newSteps);
    } else {
      const newSteps = [...verFormRollbackSteps];
      newSteps[index][field] = value;
      setVerFormRollbackSteps(newSteps);
    }
  };

  const handleRemoveStep = (type, index) => {
    if (type === 'step') {
      const newSteps = [...verFormSteps];
      newSteps.splice(index, 1);
      setVerFormSteps(newSteps);
    } else {
      const newSteps = [...verFormRollbackSteps];
      newSteps.splice(index, 1);
      setVerFormRollbackSteps(newSteps);
    }
  };

  const handleMoveStep = (type, index, direction) => {
    if (type === 'step') {
      if (index + direction < 0 || index + direction >= verFormSteps.length) return;
      const newSteps = [...verFormSteps];
      const temp = newSteps[index];
      newSteps[index] = newSteps[index + direction];
      newSteps[index + direction] = temp;
      setVerFormSteps(newSteps);
    } else {
      if (index + direction < 0 || index + direction >= verFormRollbackSteps.length) return;
      const newSteps = [...verFormRollbackSteps];
      const temp = newSteps[index];
      newSteps[index] = newSteps[index + direction];
      newSteps[index + direction] = temp;
      setVerFormRollbackSteps(newSteps);
    }
  };

  const handleSaveVersionForm = () => {
    const nameTrimmed = verFormName.trim();
    if (!nameTrimmed) {
      NotificationManager.warning('Podaj nazwę wersji aktualizacji.', 'Uwaga', 2000);
      return;
    }

    for (let i = 0; i < tempUpdateVersionsList.length; i++) {
      if (i !== editingVersionIndex && tempUpdateVersionsList[i].name.toLowerCase() === nameTrimmed.toLowerCase()) {
        NotificationManager.warning('Wersja o tej nazwie już istnieje.', 'Uwaga', 2000);
        return;
      }
    }

    const versionData = {
      name: nameTrimmed,
      steps: verFormSteps,
      rollbackSteps: verFormRollbackSteps
    };

    if (editingVersionIndex !== null) {
      setTempUpdateVersionsList(function (prev) {
        const next = prev.slice();
        next[editingVersionIndex] = versionData;
        return next;
      });
    } else {
      setTempUpdateVersionsList(function (prev) {
        return prev.concat(versionData);
      });
    }

    setEditingVersionIndex(null);
    setVerFormName('');
    setVerFormSteps([]);
    setVerFormRollbackSteps([]);
  };

  const handleEditVersionClick = (index) => {
    const ver = tempUpdateVersionsList[index];
    if (!ver) return;
    setEditingVersionIndex(index);
    setVerFormName(ver.name || '');
    setVerFormSteps(ver.steps || []);
    setVerFormRollbackSteps(ver.rollbackSteps || []);
  };

  const handleDeleteVersionClick = (index) => {
    if (editingVersionIndex === index) {
      setEditingVersionIndex(null);
      setVerFormName('');
      setVerFormSteps([]);
      setVerFormRollbackSteps([]);
    }
    setTempUpdateVersionsList(function (prev) {
      return prev.filter(function (_, i) { return i !== index; });
    });
  };

  const updateProgressPct = useMemo(() => {
    if (totalCommandsCount > 0) {
      if (completedCommandsCount >= totalCommandsCount) return 100;
      return Math.min(99, Math.round((completedCommandsCount / totalCommandsCount) * 100));
    }
    if (!updateSteps || updateSteps.length === 0) return 0;
    const completedCount = updateSteps.filter(s => s.status === 'ok' || s.status === 'skip').length;
    if (completedCount === updateSteps.length) return 100;
    return Math.round((completedCount / updateSteps.length) * 100);
  }, [totalCommandsCount, completedCommandsCount, updateSteps]);

  const executeUpdateStream = async (endpoint, payload, onSuccessMessage) => {
    try {
      const clientId = sessionStorage.getItem('clientId') || localStorage.getItem('clientId');
      const token = clientId ? localStorage.getItem(`${clientId}_token`) : '';
      
      const response = await fetch(`${API_URL}/api/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Błąd HTTP ' + response.status);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      let allSteps = [];
      let currentLog = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); 
        
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            
            if (event.type === 'init') {
              allSteps = event.steps.map(s => ({ step: s.step, name: s.name, status: 'pending', output: '' }));
              setUpdateSteps([...allSteps]);
              setTotalCommandsCount(event.totalCommands || 0);
              setCompletedCommandsCount(0);
            } 
            else if (event.type === 'step_start') {
              const stepIndex = allSteps.findIndex(s => s.step === event.step);
              if (stepIndex !== -1) {
                allSteps[stepIndex].status = 'running';
                setUpdateSteps([...allSteps]);
              }
              currentLog += `\n[Krok ${event.step}] ${event.name}: START\n`;
              setUpdateLog(currentLog);
            } 
            else if (event.type === 'cmd_start') {
              if (event.command) {
                setLastExecutedCommand(event.command);
              }
              if (event.totalCommands) {
                setTotalCommandsCount(event.totalCommands);
              }
              if (typeof event.globalCmdIndex === 'number') {
                setCompletedCommandsCount(event.globalCmdIndex);
              }
            }
            else if (event.type === 'cmd_finish') {
              if (typeof event.globalCmdIndex === 'number') {
                setCompletedCommandsCount(event.globalCmdIndex + 1);
              }
            }
            else if (event.type === 'step_finish') {
              const stepIndex = allSteps.findIndex(s => s.step === event.step);
              if (stepIndex !== -1) {
                allSteps[stepIndex].status = event.status === 'OK' ? 'ok' : 'error';
                allSteps[stepIndex].output = event.output;
                setUpdateSteps([...allSteps]);
              }
              currentLog += `${event.output ? event.output + '\n' : ''}[Krok ${event.step}] Zakończono: ${event.status}\n`;
              setUpdateLog(currentLog);
            } 
            else if (event.type === 'done') {
              if (event.success) {
                setCompletedCommandsCount(function(prev) { return Math.max(prev, totalCommandsCount); });
                NotificationManager.success(onSuccessMessage, 'Sukces', 5000);
              } else {
                setUpdateError(event.error || `Błąd w kroku ${event.failedAt}`);
                NotificationManager.error('Operacja nie powiodła się', 'Błąd', 5000);
              }
            }
          } catch (e) {
            console.error('Błąd parsowania linii:', e);
          }
        }
      }
    } catch (err) {
      setUpdateError('Błąd podczas wykonywania: ' + err.message);
      NotificationManager.error('Wystąpił błąd komunikacji', 'Błąd', 5000);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExecuteUpdate = () => {
    if (!selectedNode || !selectedNode.ipAddresses || !selectedNode.ipAddresses[0]) {
      NotificationManager.warning('Nie wybrano maszyny lub brak adresu IP.', 'Uwaga', 3000);
      return;
    }
    if (!selectedUpdateVersionName) {
      NotificationManager.warning('Wybierz wersję aktualizacji.', 'Uwaga', 2000);
      return;
    }
    if (!window.confirm(
      'Czy na pewno chcesz zaktualizować maszynę ' + (selectedNode.givenName || selectedNode.name || selectedNode.ipAddresses[0]) + '?\n\n' +
      'Wersja: ' + selectedUpdateVersionName + '\n\n' +
      'UWAGA: Proces wymaga restartu serwisów i może chwilowo zakłócić działanie automatu.'
    )) return;

    setIsUpdating(true);
    setUpdateError(null);
    setUpdateLog('');
    setUpdateSteps([]);
    setLastExecutedCommand('');
    setTotalCommandsCount(0);
    setCompletedCommandsCount(0);
    
    executeUpdateStream(
      'user/machine-update', 
      { nodeIp: selectedNode.ipAddresses[0], versionName: selectedUpdateVersionName },
      'Aktualizacja maszyny zakończona pomyślnie!'
    );
  };

  const handleRollbackUpdate = () => {
    if (!selectedNode || !selectedNode.ipAddresses || !selectedNode.ipAddresses[0]) return;
    if (!window.confirm('Czy na pewno chcesz przywrócić kopie zapasowe na maszynie ' + (selectedNode.givenName || selectedNode.name || '') + '?\nOperacja przywróci poprzednie wersje i usunie wgrane zmiany.')) return;

    setIsUpdating(true);
    setUpdateError(null);
    setUpdateLog('');
    setUpdateSteps([]);
    setLastExecutedCommand('');
    setTotalCommandsCount(0);
    setCompletedCommandsCount(0);

    executeUpdateStream(
      'user/machine-update-rollback', 
      { nodeIp: selectedNode.ipAddresses[0], versionName: selectedUpdateVersionName },
      'Rollback zakończony pomyślnie.'
    );
  };

  const handleOpenUpdateModal = () => {
    setSelectedUpdateVersionName('');
    setUpdateSteps([]);
    setUpdateLog('');
    setUpdateError(null);
    setLastExecutedCommand('');
    setTotalCommandsCount(0);
    setCompletedCommandsCount(0);
    setShowUpdateModal(true);
  };

  const handleSaveConfig = () => {
    if (activeConfigTab === 'delete-paths') {
      fetchMssqlApi(
        'user/vpn-delete-paths',
        {
          method: 'POST',
          data: { filePaths: tempFilesList },
          hideNotification: true
        },
        (response) => {
          if (response && response.success) {
            setConfigFilesList(tempFilesList);
            setShowConfigModal(false);
            NotificationManager.success('Zapisano konfigurację ścieżek plików na serwerze.', 'Sukces', 2000);
            if (deleteFilesCheckResults !== null) {
              handleCheckDeleteFiles(selectedNode, tempFilesList);
            }
          } else {
            NotificationManager.error('Nie udało się zapisać konfiguracji.', 'Błąd');
          }
        },
        () => {
          NotificationManager.error('Błąd połączenia z API podczas zapisywania konfiguracji.', 'Błąd');
        }
      );
    } else {
      fetchMssqlApi(
        'user/vpn-update-versions',
        {
          method: 'POST',
          data: { versions: tempUpdateVersionsList },
          hideNotification: true
        },
        (response) => {
          if (response && response.success) {
            setUpdateVersionsList(tempUpdateVersionsList);
            setShowConfigModal(false);
            NotificationManager.success('Zapisano wersje aktualizacji na serwerze.', 'Sukces', 2000);
          } else {
            NotificationManager.error('Nie udało się zapisać konfiguracji aktualizacji.', 'Błąd');
          }
        },
        () => {
          NotificationManager.error('Błąd połączenia z API podczas zapisywania konfiguracji aktualizacji.', 'Błąd');
        }
      );
    }
  };

  const builtInPresets = useMemo(() => [
    {
      name: 'Status zasobów i procesów',
      commands: [
        { command: 'uptime', showResponse: true },
        { command: 'free -h', showResponse: true },
        { command: 'ps aux | grep -i multivend | grep -v grep', showResponse: true }
      ]
    },
    {
      name: 'Ostatnie 30 linii logów systemowych',
      commands: [
        { command: 'sudo journalctl -u multivend -n 30 --no-pager', showResponse: true }
      ]
    }
  ], []);

  const handleOpenCustomCommandsModal = () => {
    setCustomCmdResults(null);
    setCustomCmdError(null);
    setRevealedResponses({});
    setShowSavePresetInput(false);
    setShowCustomCmdModal(true);
  };

  const handleAddCustomCommand = () => {
    setCustomCommandsList(prev => [
      ...prev,
      { id: String(Date.now() + Math.random()), command: '', showResponse: true }
    ]);
  };

  const handleUpdateCustomCommand = (index, field, value) => {
    setCustomCommandsList(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleRemoveCustomCommand = (index) => {
    setCustomCommandsList(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        return [{ id: String(Date.now()), command: '', showResponse: true }];
      }
      return next;
    });
  };

  const handleMoveCustomCommand = (index, direction) => {
    setCustomCommandsList(prev => {
      const targetIdx = index + direction;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      const temp = next[index];
      next[index] = next[targetIdx];
      next[targetIdx] = temp;
      return next;
    });
  };

  const handleClearCustomCommands = () => {
    setCustomCommandsList([{ id: String(Date.now()), command: '', showResponse: true }]);
    setCustomCmdResults(null);
    setCustomCmdError(null);
    setRevealedResponses({});
  };

  const handleApplyPreset = (preset) => {
    if (!preset || !Array.isArray(preset.commands)) return;
    const formatted = preset.commands.map(c => ({
      id: String(Date.now() + Math.random()),
      command: c.command || '',
      showResponse: c.showResponse !== undefined ? Boolean(c.showResponse) : true
    }));
    setCustomCommandsList(formatted);
    setCustomCmdResults(null);
    setCustomCmdError(null);
    setRevealedResponses({});
    NotificationManager.info(`Wczytano szablon: ${preset.name}`, 'Szablon', 2000);
  };

  const handleSaveCustomPreset = () => {
    const trimmed = newPresetName.trim();
    if (!trimmed) {
      NotificationManager.warning('Podaj nazwę szablonu.', 'Uwaga', 2000);
      return;
    }
    const validCmds = customCommandsList.filter(c => c && c.command.trim().length > 0);
    if (validCmds.length === 0) {
      NotificationManager.warning('Dodaj przynajmniej jedną komendę przed zapisaniem szablonu.', 'Uwaga', 2000);
      return;
    }
    const newPreset = {
      name: trimmed,
      commands: validCmds.map(c => ({ command: c.command.trim(), showResponse: Boolean(c.showResponse) }))
    };
    const updatedPresets = [...customPresets.filter(p => p.name.toLowerCase() !== trimmed.toLowerCase()), newPreset];
    setCustomPresets(updatedPresets);
    try {
      localStorage.setItem('vpn_custom_presets', JSON.stringify(updatedPresets));
    } catch (_) {}
    setNewPresetName('');
    setShowSavePresetInput(false);
    NotificationManager.success(`Zapisano szablon: ${trimmed}`, 'Sukces', 2000);
  };

  const handleDeleteCustomPreset = (presetName) => {
    const updated = customPresets.filter(p => p.name !== presetName);
    setCustomPresets(updated);
    try {
      localStorage.setItem('vpn_custom_presets', JSON.stringify(updated));
    } catch (_) {}
    NotificationManager.info(`Usunięto szablon: ${presetName}`, 'Szablon', 2000);
  };

  const toggleRevealResponse = (cmdIndex) => {
    setRevealedResponses(prev => ({
      ...prev,
      [cmdIndex]: !prev[cmdIndex]
    }));
  };

  const handleCopyCustomCmdReport = () => {
    if (!customCmdResults || !customCmdResults.results) return;
    const lines = [];
    lines.push(`=== RAPORT WYKONANIA CUSTOM KOMEND ===`);
    lines.push(`Maszyna: ${selectedNode ? (selectedNode.givenName || selectedNode.name) : ''} (${customCmdResults.nodeIp || ''})`);
    lines.push(`Czas trwania: ${((customCmdResults.durationMs || 0) / 1000).toFixed(2)}s`);
    lines.push(`Status końcowy: ${customCmdResults.success ? 'SUKCES' : 'BŁĄD'}`);
    lines.push(``);
    customCmdResults.results.forEach((res) => {
      lines.push(`----------------------------------------`);
      lines.push(`[Komenda #${res.index}] ${res.command}`);
      lines.push(`Status: ${res.status.toUpperCase()} | Kod wyjścia: ${res.exitCode !== null ? res.exitCode : '-'}`);
      if (res.output) {
        lines.push(`Odpowiedź:\n${res.output}`);
      } else {
        lines.push(`Odpowiedź: (brak lub ukryta)`);
      }
      lines.push(``);
    });
    navigator.clipboard.writeText(lines.join('\n'));
    NotificationManager.success('Skopiowano cały raport do schowka!', 'Sukces', 2000);
  };

  const handleExecuteCustomCommands = () => {
    if (!selectedNode || !selectedNode.ipAddresses || !selectedNode.ipAddresses[0]) {
      NotificationManager.warning('Brak wybranej maszyny lub brak adresu IP.', 'Uwaga', 2500);
      return;
    }
    const validCmds = customCommandsList.filter(c => c && c.command.trim().length > 0);
    if (validCmds.length === 0) {
      NotificationManager.warning('Wprowadź przynajmniej jedną komendę do wykonania.', 'Uwaga', 2500);
      return;
    }

    try {
      localStorage.setItem('vpn_custom_commands_last', JSON.stringify(customCommandsList));
    } catch (_) {}

    setIsCustomCmdRunning(true);
    setCustomCmdResults(null);
    setCustomCmdError(null);
    setRevealedResponses({});

    fetchMssqlApi(
      'user/remote-custom-commands',
      {
        method: 'POST',
        data: {
          nodeIp: selectedNode.ipAddresses[0],
          commands: validCmds.map(c => ({
            command: c.command.trim(),
            showResponse: Boolean(c.showResponse)
          })),
          stopOnError: customCmdStopOnError,
          timeoutMs: customCmdTimeout
        },
        hideNotification: true,
        showLoader: false,
        isErrorAcceptable: true
      },
      (res) => {
        setIsCustomCmdRunning(false);
        if (res && res.results) {
          setCustomCmdResults(res);
          if (res.success) {
            NotificationManager.success('Wszystkie komendy wykonane pomyślnie!', 'Sukces', 3000);
          } else {
            setCustomCmdError(res.error || 'Jedna lub więcej komend zakończyła się błędem.');
            NotificationManager.error(res.error || 'Błąd wykonania komend', 'Błąd', 4000);
          }
        } else {
          setCustomCmdError((res && res.error) || 'Niepoprawna odpowiedź z serwera.');
          NotificationManager.error((res && res.error) || 'Błąd wykonania', 'Błąd');
        }
      },
      (err) => {
        setIsCustomCmdRunning(false);
        const msg = (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Błąd połączenia z API';
        setCustomCmdError(msg);
        NotificationManager.error(msg, 'Błąd komunikacji');
      }
    );
  };

  const paginatedErrors = useMemo(() => {
    if (!errorsData) return [];
    const startIndex = (errorsPage - 1) * 10;
    return errorsData.slice(startIndex, startIndex + 10);
  }, [errorsData, errorsPage]);

  const totalErrorsPages = useMemo(() => {
    if (!errorsData) return 0;
    return Math.ceil(errorsData.length / 10);
  }, [errorsData]);

  const runScript = (scriptPath, scriptLabel) => {
    if (!selectedNode || !selectedNode.ipAddresses || !selectedNode.ipAddresses[0]) return;
    if (!window.confirm(`Czy na pewno chcesz uruchomić skrypt: ${scriptLabel}?`)) return;

    const nodeIp = selectedNode.ipAddresses[0];

    setRunningScripts((prev) => {
      const next = Object.assign({}, prev);
      next[scriptPath] = true;
      return next;
    });

    setScriptOutputs((prev) => {
      const next = Object.assign({}, prev);
      delete next[scriptPath];
      return next;
    });

    fetchMssqlApi(
      'user/run-remote-script',
      {
        method: 'POST',
        data: { nodeIp, scriptPath },
        hideNotification: true
      },
      (res) => {
        setRunningScripts((prev) => {
          const next = Object.assign({}, prev);
          next[scriptPath] = false;
          return next;
        });
        setScriptOutputs((prev) => {
          const next = Object.assign({}, prev);
          next[scriptPath] = { success: true, text: res && res.output };
          return next;
        });
      },
      (err) => {
        setRunningScripts((prev) => {
          const next = Object.assign({}, prev);
          next[scriptPath] = false;
          return next;
        });
        setScriptOutputs((prev) => {
          const next = Object.assign({}, prev);
          next[scriptPath] = { success: false, text: (err && err.message) || 'Błąd połączenia z API' };
          return next;
        });
      }
    );
  };

  const getCrontabValue = (settings) => {
    if (!Array.isArray(settings)) return null;
    for (let i = 0; i < settings.length; i++) {
      const row = settings[i];
      if (!row) continue;
      const keys = Object.keys(row);
      for (let j = 0; j < keys.length; j++) {
        const k = keys[j];
        if (row[k] && String(row[k]).toLowerCase().trim() === 'crontab') {
          for (let m = 0; m < keys.length; m++) {
            const valKey = keys[m];
            if (valKey !== k && row[valKey] && String(row[valKey]).trim().startsWith('{')) {
              return row[valKey];
            }
          }
        }
      }
    }
    return null;
  };

  const getOfflineDuration = (lastSeenTime) => {
    if (!lastSeenTime) return 'unknown length';

    const lastSeenParsed = new Date(lastSeenTime);
    if (Number.isNaN(lastSeenParsed.getTime())) return 'unknown length';

    const diffMs = timeTicker - lastSeenParsed.getTime();
    if (diffMs < 0) return 'just now';

    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const filteredAndSortedNodes = useMemo(() => {
    const nodeList = (statusData.headscale && statusData.headscale.nodes) || [];
    const searchLower = nodeSearch.toLowerCase().trim();
    
    const filtered = nodeList.filter(node => {
      if (!node) return false;
      if (!showOffline && !node.online) return false;
      const vdmName = (node.vdmName || node.givenName || node.name || '').toLowerCase();
      const user = (node.user || '').toLowerCase();
      const client = (node.customerAbbr || '').toLowerCase();
      const matchesIp = (node.ipAddresses && node.ipAddresses.some(ip => String(ip || '').toLowerCase().includes(searchLower))) || false;
      return vdmName.includes(searchLower) || user.includes(searchLower) || client.includes(searchLower) || matchesIp;
    });
    return filtered.sort((a, b) => {
      if (a.online !== b.online) {
        return a.online ? -1 : 1;
      }
      
      const clientA = a.customerAbbr || '';
      const clientB = b.customerAbbr || '';
      if (clientA !== clientB) {
        if (!clientA) return 1;
        if (!clientB) return -1;
        return clientA.localeCompare(clientB);
      }
      
      const nameA = a.vdmName || a.givenName || a.name || '';
      const nameB = b.vdmName || b.givenName || b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [statusData.headscale, nodeSearch, showOffline]);

  const paginatedNodes = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedNodes.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedNodes, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedNodes.length / itemsPerPage);


  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12 col-xxl-11 mx-auto">
          
          {loadError ? <div className="alert alert-warning mb-4">{loadError}</div> : null}

          <VpnSqlPanel nodes={(statusData.headscale && statusData.headscale.nodes) || []} />
          <div className="row">
            <div className="col-12 mb-4">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white border-0 pb-0 d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex align-items-center mb-2 mb-md-0">
                    <i className="fas fa-network-wired text-primary mr-2" />
                    <h2 className="h5 mb-0">Headscale VPN</h2>
                    {filteredAndSortedNodes.length > 0 && (
                      <span className="badge badge-light ml-2 text-muted font-weight-normal">
                        {((currentPage - 1) * itemsPerPage) + 1}-
                        {Math.min(filteredAndSortedNodes.length, currentPage * itemsPerPage)} z {filteredAndSortedNodes.length}
                      </span>
                    )}
                    <span className="badge badge-primary badge-pill ml-3 px-2 py-1" style={{ fontSize: '9px', fontWeight: '500' }}>
                      Odświeżanie co {refreshEveryMinutes} min
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm ml-3 px-2 py-0.5 rounded shadow-sm d-inline-flex align-items-center"
                      style={{ fontSize: '9px', fontWeight: 'bold', border: '1px solid #ced4da', height: '22px', cursor: 'pointer' }}
                      onClick={handleOpenConfigModal}
                      title="Konfiguracja"
                    >
                      <i className="fas fa-cog" />
                    </button>
                  </div>
                  <div className="d-flex align-items-center mb-2 mb-md-0" style={{ gap: '15px' }}>
                    <div className="custom-control custom-checkbox custom-control-inline mb-0">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="toggleOfflineCheckbox"
                        checked={showOffline}
                        onChange={(e) => setShowOffline(e.target.checked)}
                      />
                      <label className="custom-control-label small font-weight-bold text-secondary mb-0" htmlFor="toggleOfflineCheckbox" style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Pokazuj offline
                      </label>
                    </div>
                    <div style={{ width: '180px' }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Wyszukaj maszynę..."
                        value={nodeSearch}
                        onChange={(e) => setNodeSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {isLoading ? (
                    <div className="text-muted small">Ładowanie serwera headscale...</div>
                  ) : (statusData.headscale && statusData.headscale.error) ? (
                    <div className="alert alert-danger small py-2 mb-0">{statusData.headscale.error}</div>
                  ) : filteredAndSortedNodes.length === 0 ? (
                    <div className="text-muted small">No machines matching the query filter.</div>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap mb-2" style={{ gap: '10px' }}>
                        {paginatedNodes.map((node) => (
                          <div
                            key={node.id}
                            className="card border px-3 py-2 d-flex flex-row align-items-center shadow-sm rounded"
                            onClick={() => handleNodeClick(node)}
                            style={{
                              minWidth: '220px', flexGrow: 1, maxWidth: '320px',
                              backgroundColor: node.online ? '#f4fbf7' : '#fff5f5',
                              borderLeft: node.online ? '4px solid #28a745' : '4px solid #dc3545',
                              cursor: node.online ? 'pointer' : 'not-allowed',
                              transition: 'transform 0.1s ease'
                            }}
                            onMouseEnter={(e) => node.online && (e.currentTarget.style.transform = 'translateY(-2px)')}
                            onMouseLeave={(e) => node.online && (e.currentTarget.style.transform = 'translateY(0)')}
                            title={node.online ? "Click to query machine disk space" : `Offline for ${getOfflineDuration(node.lastSeen)}`}
                          >
                            <div className="w-100">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="font-weight-bold text-truncate pr-2 text-dark" title={node.givenName || node.name}>
                                  {node.vdmName || node.givenName || node.name}
                                </span>
                                {node.online ? (
                                  <span className="badge badge-success badge-pill small">Online</span>
                                ) : (
                                  <span className="badge badge-danger badge-pill small" title={`Last seen: ${node.lastSeen}`}>
                                    Off: {getOfflineDuration(node.lastSeen)}
                                  </span>
                                )}
                              </div>
                              <div className="text-muted small d-flex justify-content-between">
                                <span className="text-truncate">
                                  <i className="fas fa-user mr-1 text-secondary" />
                                  <span className="font-weight-bold" style={{ color: getClientColor(node.customerAbbr) }}>
                                    {node.customerAbbr ? String(node.customerAbbr).toLowerCase() : '-'}
                                  </span>
                                </span>
                                <span className="text-monospace font-weight-light text-muted small">{(node.ipAddresses && node.ipAddresses[0]) || ''}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center mt-4">
                          <nav>
                            <ul className="pagination pagination-sm mb-0">
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" type="button" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Previous</button>
                              </li>
                              {[...Array(totalPages)].map((_, index) => (
                                <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                  <button className="page-link" type="button" onClick={() => setCurrentPage(index + 1)}>{index + 1}</button>
                                </li>
                              ))}
                              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" type="button" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>Next</button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {selectedNode && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
              <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-header bg-light border-0">
                    <h5 className="modal-title font-weight-bold">
                      <i className="fas fa-terminal mr-2 text-dark" />
                      {selectedNode.givenName || selectedNode.name}
                    </h5>
                    <button type="button" className="close" onClick={() => setSelectedNode(null)}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body p-4">
                    <div className="card mb-4 border border-light shadow-sm bg-light">
                      <div className="card-body p-3">
                        <h6 className="font-weight-bold mb-3 d-flex align-items-center text-dark">
                          <i className="fas fa-desktop mr-2 text-primary" /> Zdalny dostęp
                        </h6>
                        <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                          <a href={`ssh://pi@${(selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || ''}`} className="btn btn-primary btn-sm rounded shadow-sm">
                            <i className="fas fa-terminal mr-2" /> Uruchom SSH (ssh://)
                          </a>
                          <a href={`vnc://${(selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || ''}`} className="btn btn-info btn-sm rounded shadow-sm text-white">
                            <i className="fas fa-tv mr-2" /> Uruchom VNC (vnc://)
                          </a>
                          <button 
                            type="button" 
                            className="btn btn-outline-dark btn-sm rounded shadow-sm"
                            onClick={() => {
                              const ip = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
                              navigator.clipboard.writeText(`ssh pi@${ip}`);
                              NotificationManager.success(`Skopiowano komendę: ssh pi@${ip}`, 'Sukces', 2000);
                            }}
                          >
                            <i className="fas fa-copy mr-2" /> Kopiuj komendę SSH
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-outline-dark btn-sm rounded shadow-sm"
                            onClick={() => {
                              const ip = (selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || '';
                              navigator.clipboard.writeText(ip);
                              NotificationManager.success(`Skopiowano IP: ${ip}`, 'Sukces', 2000);
                            }}
                          >
                            <i className="fas fa-copy mr-2" /> Kopiuj IP
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-success btn-sm rounded shadow-sm text-white font-weight-bold"
                            onClick={handleCheckChoices}
                            disabled={isChoicesLoading}
                          >
                            {isChoicesLoading ? (
                              <span><i className="fas fa-spinner fa-spin mr-2" /> Pobieranie...</span>
                            ) : (
                              <span><i className="fas fa-list mr-2" /> Sprawdź wybory</span>
                            )}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-danger btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={handleCheckErrors}
                            disabled={isErrorsLoading}
                          >
                            {isErrorsLoading ? (
                              <span><i className="fas fa-spinner fa-spin mr-2" /> Pobieranie...</span>
                            ) : (
                              <span><i className="fas fa-exclamation-triangle mr-2" /> Sprawdź błędy</span>
                            )}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-warning btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={handleCheckEncoding}
                            disabled={isEncodingLoading}
                          >
                            {isEncodingLoading ? (
                              <span><i className="fas fa-spinner fa-spin mr-2" /> Pobieranie...</span>
                            ) : (
                              <span><i className="fas fa-file-code mr-2" /> Sprawdź kodowanie plików</span>
                            )}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-primary btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={handleCheckTransactions}
                            disabled={isTrxLoading}
                          >
                            {isTrxLoading ? (
                              <span><i className="fas fa-spinner fa-spin mr-2" /> Pobieranie...</span>
                            ) : (
                              <span><i className="fas fa-receipt mr-2" /> Sprawdź ostatnie 5 transakcji</span>
                            )}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={() => {
                              if (deleteFilesCheckResults === null) {
                                handleCheckDeleteFiles();
                              } else {
                                setDeleteFilesCheckResults(null);
                              }
                            }}
                            disabled={isDeleteFilesChecking}
                          >
                            {isDeleteFilesChecking ? (
                              <span><i className="fas fa-spinner fa-spin mr-2" /> Pobieranie...</span>
                            ) : (
                              <span><i className="fas fa-trash-alt mr-2" /> Pliki do usunięcia</span>
                            )}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-dark btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={handleCheckHeartbeat}
                            disabled={isHeartbeatChecking}
                          >
                            {isHeartbeatChecking ? (
                              <span><i className="fas fa-spinner fa-spin mr-2" /> Sprawdzanie...</span>
                            ) : (
                              <span><i className="fas fa-heartbeat mr-2" /> Sprawdź Heartbeat</span>
                            )}
                          </button>
                          <button type="button" className="btn btn-dark btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={() => handleInitPayment()} disabled={isInitPaymentChecking || isInitPaymentFixing}>
                            <i className={`fas ${isInitPaymentChecking ? 'fa-spinner fa-spin' : 'fa-credit-card'} mr-2`} />
                            {isInitPaymentChecking ? 'Sprawdzanie...' : 'Sprawdź init payment'}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-info btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={handleOpenUpdateModal}
                          >
                            <span><i className="fas fa-upload mr-2" /> Aktualizacja</span>
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-dark btn-sm rounded shadow-sm text-white font-weight-bold ml-2"
                            onClick={handleOpenCustomCommandsModal}
                          >
                            <span><i className="fas fa-terminal mr-2 text-warning" /> Custom komendy</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {choicesData && (
                      <div className="card mb-4 border border-light shadow-sm bg-white p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="font-weight-bold text-dark mb-0">
                            <i className="fas fa-list mr-2 text-success" />
                            Wybory maszynowe (tblMachineProduct)
                          </h6>
                          <button 
                            type="button" 
                            className="close" 
                            onClick={() => setChoicesData(null)}
                            style={{ fontSize: '18px', padding: '0', background: 'transparent', border: '0' }}
                          >
                            <span>&times;</span>
                          </button>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                          <table className="table table-sm table-striped table-bordered mb-0 text-dark" style={{ fontSize: '11px' }}>
                            <thead className="bg-light text-secondary font-weight-bold">
                              <tr>
                                <th>MachineFeederNo</th>
                                <th>Quantity</th>
                                <th>MaxItemCount</th>
                                <th>IsLocked</th>
                              </tr>
                            </thead>
                            <tbody>
                              {choicesData.length === 0 ? (
                                <tr>
                                  <td colSpan="4" className="text-center text-muted py-2">Brak danych w bazie maszyn</td>
                                </tr>
                              ) : (
                                choicesData.map((choice, idx) => (
                                  <tr key={idx}>
                                    <td className="font-weight-bold">{choice.MachineFeederNo}</td>
                                    <td>{choice.Quantity}</td>
                                    <td>{choice.MaxItemCount}</td>
                                    <td>
                                      {Number(choice.IsLocked) === 1 || choice.IsLocked === '1' || choice.IsLocked === 'true' || choice.IsLocked === true ? (
                                        <span className="badge badge-danger">Zablokowany</span>
                                      ) : (
                                        <span className="badge badge-success">Odblokowany</span>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {errorsData && (
                      <div className="card mb-4 border border-light shadow-sm bg-white p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="font-weight-bold text-dark mb-0">
                            <i className="fas fa-exclamation-triangle mr-2 text-danger" />
                            Błędy maszynowe (tblError)
                            <span className="badge badge-light ml-2 text-muted font-weight-normal" style={{ fontSize: '11px' }}>
                              Strona {errorsPage} z {totalErrorsPages} (wszystkich: {errorsData.length})
                            </span>
                          </h6>
                          <button 
                            type="button" 
                            className="close" 
                            onClick={() => setErrorsData(null)}
                            style={{ fontSize: '18px', padding: '0', background: 'transparent', border: '0' }}
                          >
                            <span>&times;</span>
                          </button>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                          <table className="table table-sm table-striped table-bordered mb-0 text-dark" style={{ fontSize: '11px' }}>
                            <thead className="bg-light text-secondary font-weight-bold">
                              <tr>
                                <th>ErrorId</th>
                                <th>CreateDateTime</th>
                                <th>ErrorCode</th>
                                <th>MachineFeederNo</th>
                                <th>IsActive</th>
                                <th>MachineType</th>
                              </tr>
                            </thead>
                            <tbody>
                              {errorsData.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="text-center text-muted py-2">Brak danych w bazie maszyn</td>
                                </tr>
                              ) : (
                                paginatedErrors.map((errorItem, idx) => (
                                  <tr key={idx}>
                                    <td className="font-weight-bold">{errorItem.ErrorId}</td>
                                    <td>{errorItem.CreateDateTime}</td>
                                    <td>{errorItem.ErrorCode}</td>
                                    <td>{errorItem.MachineFeederNo}</td>
                                    <td>
                                      {Number(errorItem.IsActive) === 1 || errorItem.IsActive === '1' || errorItem.IsActive === 'true' || errorItem.IsActive === true ? (
                                        <span className="badge badge-danger">Aktywny</span>
                                      ) : (
                                        <span className="badge badge-secondary">Nieaktywny</span>
                                      )}
                                    </td>
                                    <td>{errorItem.MachineType}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        {totalErrorsPages > 1 && (
                          <div className="d-flex justify-content-center align-items-center mt-3">
                            <nav>
                              <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${errorsPage === 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    type="button" 
                                    onClick={() => setErrorsPage(prev => Math.max(prev - 1, 1))}
                                  >
                                    Poprzednia
                                  </button>
                                </li>
                                {[...Array(totalErrorsPages)].map((_, index) => (
                                  <li key={index + 1} className={`page-item ${errorsPage === index + 1 ? 'active' : ''}`}>
                                    <button 
                                      className="page-link" 
                                      type="button" 
                                      onClick={() => setErrorsPage(index + 1)}
                                    >
                                      {index + 1}
                                    </button>
                                  </li>
                                ))}
                                <li className={`page-item ${errorsPage === totalErrorsPages ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    type="button" 
                                    onClick={() => setErrorsPage(prev => Math.min(prev + 1, totalErrorsPages))}
                                  >
                                    Następna
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        )}
                      </div>
                    )}

                    {filesEncodingData && (
                      <div className="card mb-4 border border-light shadow-sm bg-white p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="font-weight-bold text-dark mb-0">
                            <i className="fas fa-file-code mr-2 text-warning" />
                            Kodowanie plików skryptowych
                          </h6>
                          <button 
                            type="button" 
                            className="close" 
                            onClick={() => setFilesEncodingData(null)}
                            style={{ fontSize: '18px', padding: '0', background: 'transparent', border: '0' }}
                          >
                            <span>&times;</span>
                          </button>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-sm table-striped table-bordered mb-0 text-dark" style={{ fontSize: '11px' }}>
                            <thead className="bg-light text-secondary font-weight-bold">
                              <tr>
                                <th>Ścieżka pliku</th>
                                <th>Status</th>
                                <th>Szczegóły</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filesEncodingData.map((item, idx) => (
                                <tr key={idx}>
                                  <td className="font-weight-bold text-monospace">{item.file}</td>
                                  <td>
                                    {item.status === 'OK' ? (
                                      <span className="badge badge-success">Poprawne (LF)</span>
                                    ) : item.status === 'CRLF' ? (
                                      <span className="badge badge-warning">Błędne (CRLF)</span>
                                    ) : (
                                      <span className="badge badge-danger">Brak pliku</span>
                                    )}
                                  </td>
                                  <td>{item.detail}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {trxData && (
                      <div className="card mb-4 border border-light shadow-sm bg-white p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="font-weight-bold text-dark mb-0">
                            <i className="fas fa-receipt mr-2 text-primary" />
                            Ostatnie 5 transakcji (tblTransaction)
                          </h6>
                          <button 
                            type="button" 
                            className="close" 
                            onClick={() => setTrxData(null)}
                            style={{ fontSize: '18px', padding: '0', background: 'transparent', border: '0' }}
                          >
                            <span>&times;</span>
                          </button>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-sm table-striped table-bordered mb-0 text-dark" style={{ fontSize: '11px' }}>
                            <thead className="bg-light text-secondary font-weight-bold">
                              <tr>
                                <th>TransactionId</th>
                                <th>UUID</th>
                                <th>GiveAwayNo</th>
                                <th>Name</th>
                                <th>PaymentType</th>
                                <th>MachineFeederNo</th>
                                <th>EAN</th>
                                <th>Price</th>
                                <th>CreateDateTime</th>
                                <th>UpdateDateTime</th>
                              </tr>
                            </thead>
                            <tbody>
                              {trxData.length === 0 ? (
                                <tr>
                                  <td colSpan="10" className="text-center text-muted py-2">Brak transakcji w bazie danych automatu</td>
                                </tr>
                              ) : (
                                trxData.map((trx, idx) => (
                                  <tr key={idx}>
                                    <td className="font-weight-bold">{trx.TransactionId}</td>
                                    <td className="text-monospace" style={{ fontSize: '10px' }}>{trx.UUID}</td>
                                    <td>{trx.GiveAwayNo}</td>
                                    <td>
                                      {trx.Name === 'Vend success' || trx.Name === 'Product received' ? (
                                        <span className="badge badge-success">{trx.Name}</span>
                                      ) : (
                                        <span className="badge badge-danger">{trx.Name}</span>
                                      )}
                                    </td>
                                    <td>{trx.PaymentType}</td>
                                    <td>{trx.MachineFeederNo}</td>
                                    <td>{trx.EAN}</td>
                                    <td className="font-weight-bold">{trx.Price} zł</td>
                                    <td>{trx.CreateDateTime}</td>
                                    <td>{trx.UpdateDateTime}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {deleteFilesCheckResults && (
                      <div className="card mb-4 border border-light shadow-sm bg-white p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="font-weight-bold text-dark mb-0">
                            <i className="fas fa-trash-alt mr-2 text-danger" />
                            Pliki do usunięcia
                          </h6>
                          <div className="d-flex" style={{ gap: '10px' }}>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm rounded shadow-sm font-weight-bold"
                              onClick={() => handleCheckDeleteFiles()}
                              disabled={isDeleteFilesChecking}
                            >
                              {isDeleteFilesChecking ? (
                                <span><i className="fas fa-spinner fa-spin mr-1" /> Sprawdzanie...</span>
                              ) : (
                                <span><i className="fas fa-sync-alt mr-1" /> Sprawdź ponownie</span>
                              )}
                            </button>
                            <button 
                              type="button" 
                              className="close" 
                              onClick={() => setDeleteFilesCheckResults(null)}
                              style={{ fontSize: '18px', padding: '0', background: 'transparent', border: '0' }}
                            >
                              <span>&times;</span>
                            </button>
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-sm table-striped table-bordered mb-0 text-dark" style={{ fontSize: '11px' }}>
                            <thead className="bg-light text-secondary font-weight-bold">
                              <tr>
                                <th>Ścieżka pliku</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deleteFilesCheckResults.length === 0 ? (
                                <tr>
                                  <td colSpan="2" className="text-center text-muted py-3">
                                    Brak skonfigurowanych ścieżek plików. Dodaj je za pomocą ikony konfiguracji obok badge odświeżania.
                                  </td>
                                </tr>
                              ) : (
                                deleteFilesCheckResults.map((item, idx) => {
                                  return (
                                    <tr key={idx}>
                                      <td className="font-weight-bold text-monospace align-middle">{item.file}</td>
                                      <td className="align-middle">
                                        {item.exists ? (
                                          <span className="badge badge-danger">Istnieje</span>
                                        ) : (
                                          <span className="badge badge-success">Brak pliku</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {initPaymentCheckData && (
                      <div className="card mb-4 border border-light shadow-sm bg-white p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="font-weight-bold text-dark mb-0">Status init payment</h6>
                          <button type="button" className="close" aria-label="Zamknij status init payment" onClick={() => setInitPaymentCheckData(null)}>&times;</button>
                        </div>
                        <div className={`alert ${initPaymentCheckData.isCurrent ? 'alert-success' : 'alert-danger'} small py-2 mb-0 d-flex align-items-center justify-content-between`}>
                          <span>{initPaymentCheckData.isCurrent
                            ? 'Init payment jest aktualny — czeka na zakończenie płatności w pętli co 15 sekund.'
                            : initPaymentCheckData.exists === false ? 'Brak pliku init-payment.sh. Wymagana instalacja.' : 'Init payment jest nieaktualny. Wymagana aktualizacja skryptu.'}</span>
                          {!initPaymentCheckData.isCurrent && (
                            <button type="button" className="btn btn-danger btn-sm ml-3" onClick={() => handleInitPayment(true)} disabled={isInitPaymentFixing || isInitPaymentChecking}>
                              {isInitPaymentFixing ? 'Aktualizowanie...' : 'Popraw'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {heartbeatCheckData && (
                      <div className="card mb-4 border border-light shadow-sm bg-white p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="font-weight-bold text-dark mb-0">
                            <i className="fas fa-heartbeat mr-2 text-danger" />
                            Status Heartbeat
                          </h6>
                          <button 
                            type="button" 
                            className="close" 
                            onClick={() => setHeartbeatCheckData(null)}
                            style={{ fontSize: '18px', padding: '0', background: 'transparent', border: '0' }}
                          >
                            <span>&times;</span>
                          </button>
                        </div>
                        <div className={`alert ${heartbeatCheckData.isCurrent ? 'alert-success' : 'alert-danger'} small py-2 mb-0 d-flex align-items-center justify-content-between`}>
                          <div>
                            <i className={`fas ${heartbeatCheckData.isCurrent ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`} />
                            {heartbeatCheckData.isCurrent 
                              ? 'Heartbeat jest aktualny (zawiera port 10443).' 
                              : 'Heartbeat jest nieaktualny (brak portu 10443). Wymagana aktualizacja plików.'}
                          </div>
                          {!heartbeatCheckData.isCurrent && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm rounded shadow-sm font-weight-bold ml-3 px-3"
                              onClick={handleFixHeartbeat}
                              disabled={isHeartbeatFixing}
                            >
                              {isHeartbeatFixing ? (
                                <span><i className="fas fa-spinner fa-spin mr-1" /> Naprawianie...</span>
                              ) : (
                                <span><i className="fas fa-wrench mr-1" /> Popraw</span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {!hasConnected ? (
                      <div className="text-center py-5 bg-white border rounded shadow-sm">
                        <i className="fas fa-plug fa-3x mb-3 text-muted" />
                        <h5 className="font-weight-bold text-dark mb-2">Brak aktywnego połączenia</h5>
                        <p className="small text-muted px-4 mb-4" style={{ maxWidth: '480px', margin: '0 auto' }}>
                          Aby pobrać zużycie dysku, zadania crontab oraz transfer sieciowy, nawiąż bezpośrednie połączenie z urządzeniem.
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm px-4 py-2 rounded shadow-sm font-weight-bold"
                          onClick={handleConnect}
                        >
                          <i className="fas fa-link mr-2" /> Połącz
                        </button>
                      </div>
                    ) : (
                      <>
                        {isModalLoading ? (
                          <div className="text-center py-5 text-muted bg-white border rounded shadow-sm">
                            <i className="fas fa-spinner fa-spin fa-2x mb-3 text-primary" />
                            <div>Próba łączenia z serwerem...</div>
                          </div>
                        ) : nodeDiskData ? (
                          <div>
                            <p className="small text-muted mb-3">
                              Połączono z <code>pi@{(selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || ''}</code>
                            </p>
                            <div className="card border-0 bg-white p-3 shadow-sm rounded border">
                              <h6 className="font-weight-bold text-dark mb-3">
                                <i className="fas fa-hdd mr-2 text-secondary" />
                                Użycie dysku ({nodeDiskData.filesystem})
                              </h6>
                              <div className="d-flex align-items-center justify-content-between mb-1 small font-weight-bold">
                                <span>Zapełnienie dysku: {nodeDiskData.pct}%</span>
                                <span className="text-muted">{nodeDiskData.used} z {nodeDiskData.size}</span>
                              </div>
                              <div className="progress mb-3" style={{ height: '18px', borderRadius: '4px', backgroundColor: '#e9ecef' }}>
                                <div 
                                  className={`progress-bar progress-bar-striped progress-bar-animated ${
                                    nodeDiskData.pct >= 90 ? 'bg-danger' : nodeDiskData.pct >= 70 ? 'bg-warning' : 'bg-success'
                                  }`}
                                  role="progressbar" 
                                  style={{ width: `${nodeDiskData.pct}%`, fontSize: '11px', fontWeight: 'bold' }} 
                                  aria-valuenow={nodeDiskData.pct} 
                                  aria-valuemin="0" 
                                  aria-valuemax="100"
                                >
                                  {nodeDiskData.pct}%
                                </div>
                              </div>
                              <div className="row text-center mt-2 small">
                                <div className="col-6 border-right">
                                  <div className="text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>Wolne miejsce</div>
                                  <div className="font-weight-bold text-success h6 mb-0 mt-1">{nodeDiskData.avail}</div>
                                </div>
                                <div className="col-6">
                                  <div className="text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>Użyte miejsce</div>
                                  <div className="font-weight-bold text-dark h6 mb-0 mt-1">{nodeDiskData.used}</div>
                                </div>
                              </div>
                            </div>

                            {nodeNetworkData && nodeNetworkData.length > 0 ? (
                              <div className="card border-0 bg-white p-3 shadow-sm rounded border mt-3">
                                <h6 className="font-weight-bold text-dark mb-3">
                                  <i className="fas fa-network-wired mr-2 text-info" />
                                  Zużycie transferu sieciowego
                                </h6>
                                <div className="table-responsive">
                                  <table className="table table-sm table-hover mb-0 text-dark" style={{ fontSize: '11px' }}>
                                    <thead className="bg-light text-secondary font-weight-bold">
                                      <tr>
                                        <th className="border-0 py-2">Interfejs</th>
                                        <th className="border-0 py-2 text-right">Pobrano (RX)</th>
                                        <th className="border-0 py-2 text-right">Wysłano (TX)</th>
                                        <th className="border-0 py-2 text-right">Łącznie</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {nodeNetworkData.map((iface, idx) => (
                                        <tr key={idx}>
                                          <td className="align-middle py-2 font-weight-bold">{iface.interface}</td>
                                          <td className="align-middle py-2 text-right text-success">{iface.rx}</td>
                                          <td className="align-middle py-2 text-right text-primary">{iface.tx}</td>
                                          <td className="align-middle py-2 text-right font-weight-bold">{iface.total}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null}

                            {nodeSettingsData ? (
                              <div className="card border-0 bg-white p-3 shadow-sm rounded border mt-3">
                                <h6 className="font-weight-bold text-dark mb-3">
                                  <i className="fas fa-cog mr-2 text-primary" />
                                  Czy aktywne?
                                </h6>
                                {nodeSettingsData.error ? (
                                  <div className="alert alert-danger small mb-0">
                                    {nodeSettingsData.error}
                                  </div>
                                ) : (() => {
                                  const crontabVal = getCrontabValue(nodeSettingsData);
                                  if (!crontabVal) {
                                    return <div className="text-muted small text-center py-2">Nie znaleziono ustawień crontab.</div>;
                                  }
                                  try {
                                    const parsed = JSON.parse(crontabVal);
                                    const loadAll = parsed.loadAllTransactions;
                                    const hb = parsed.heartbeat;

                                    return (
                                      <div className="row text-center mt-2 font-weight-bold">
                                        <div className="col-6 pr-1">
                                          <div 
                                            className="p-2 rounded border text-dark shadow-sm bg-white" 
                                            style={{ 
                                              borderLeft: loadAll && loadAll.Checked ? '3px solid #28a745' : '3px solid #6c757d',
                                              minHeight: '65px',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              justifyContent: 'center',
                                              alignItems: 'center'
                                            }}
                                          >
                                            <div style={{ fontSize: '9px', textTransform: 'uppercase', color: '#6c757d', letterSpacing: '0.5px' }}>loadAllTransactions</div>
                                            <span className={`badge mt-1 text-white px-2 py-0.5 ${loadAll && loadAll.Checked ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '8px', lineHeight: '1.2' }}>
                                              {loadAll && loadAll.Checked ? 'Aktywny' : 'Wyłączony'}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="col-6 pl-1">
                                          <div 
                                            className="p-2 rounded border text-dark shadow-sm bg-white" 
                                            style={{ 
                                              borderLeft: hb && hb.Checked ? '3px solid #28a745' : '3px solid #6c757d',
                                              minHeight: '65px',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              justifyContent: 'center',
                                              alignItems: 'center'
                                            }}
                                          >
                                            <div style={{ fontSize: '9px', textTransform: 'uppercase', color: '#6c757d', letterSpacing: '0.5px' }}>heartbeat</div>
                                            <span className={`badge mt-1 text-white px-2 py-0.5 ${hb && hb.Checked ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '8px', lineHeight: '1.2' }}>
                                              {hb && hb.Checked ? 'Aktywny' : 'Wyłączony'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  } catch (e) {
                                    return <div className="text-danger small text-center py-2">Błąd parsowania konfiguracji crontab.</div>;
                                  }
                                })()}
                              </div>
                            ) : null}

                            <div className="card border-0 bg-white p-3 shadow-sm rounded border mt-3">
                              <h6 className="font-weight-bold text-dark mb-3">
                                <i className="fas fa-terminal mr-2 text-warning" />
                                Uruchamianie skryptów zdalnych
                              </h6>
                              <div className="d-flex flex-column" style={{ gap: '10px' }}>
                                {[
                                  { label: 'heartbeat.sh', path: 'sudo bash /home/pi/Documents/sql/evadts/heartbeat.sh' },
                                  { label: 'load_all_transactions.sh', path: 'sudo bash /home/pi/Documents/sql/evadts/load_all_transactions.sh' },
                                  { label: 'init-payment.sh', path: 'sudo bash /home/pi/Documents/vendim-rest-api/sh/init-payment.sh' },
                                  { label: 'clear-log.sh', path: 'sudo bash /home/pi/Documents/vendim-rest-api/sh/clear-log.sh' }
                                ].map((script, sIdx) => {
                                  const isRunning = !!runningScripts[script.path];
                                  const output = scriptOutputs[script.path];

                                  return (
                                    <div key={sIdx} className="p-2 rounded bg-light border-left border-warning" style={{ borderWidth: '3px' }}>
                                      <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex flex-column">
                                          <span className="font-weight-bold text-dark" style={{ fontSize: '11px' }}>{script.label}</span>
                                          <code className="text-muted" style={{ fontSize: '9px', marginTop: '2px' }}>{script.path}</code>
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-warning btn-sm rounded shadow-sm px-3"
                                          style={{ fontSize: '10px', fontWeight: 'bold' }}
                                          onClick={() => runScript(script.path, script.label)}
                                          disabled={isRunning}
                                        >
                                          {isRunning ? (
                                            <span><i className="fas fa-spinner fa-spin mr-1" /> Uruchamianie...</span>
                                          ) : (
                                            <span><i className="fas fa-play mr-1" /> Uruchom</span>
                                          )}
                                        </button>
                                      </div>
                                      {output ? (
                                        <div className="mt-2 p-2 rounded bg-white border text-monospace" style={{ fontSize: '9px', maxHeight: '100px', overflowY: 'auto', whiteSpace: 'pre-wrap', color: output.success ? '#155724' : '#721c24', backgroundColor: output.success ? '#d4edda' : '#f8d7da', borderColor: output.success ? '#c3e6cb' : '#f5c6cb' }}>
                                          {output.text}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="alert alert-warning mb-0 p-4 text-center border shadow-sm bg-white rounded">
                            <i className="fas fa-exclamation-triangle fa-2x mb-3 text-warning" />
                            <h6 className="font-weight-bold text-dark mb-2">Brak połączenia z maszyną</h6>
                            <p className="small text-muted mb-4" style={{ maxWidth: '520px', margin: '0 auto' }}>
                              Nie udało się nawiązać połączenia SSH z <code>pi@{(selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || ''}</code>.
                              Upewnij się, że zapora sieciowa zezwala na port 22 oraz klucz publiczny jest prawidłowo skonfigurowany.
                            </p>
                            <button
                              type="button"
                              className="btn btn-warning btn-sm px-4 py-2 rounded shadow-sm font-weight-bold text-white"
                              onClick={handleConnect}
                            >
                              <i className="fas fa-sync-alt mr-2" /> Ponów połączenie
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="modal-footer border-0 bg-light">
                    <button type="button" className="btn btn-secondary btn-sm rounded shadow-sm" onClick={() => setSelectedNode(null)}>
                      Zamknij sesje
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showConfigModal && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
              <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-header bg-light border-0">
                    <h5 className="modal-title font-weight-bold">
                      <i className="fas fa-cog mr-2 text-dark" />
                      Ustawienia VPN
                    </h5>
                    <button type="button" className="close" onClick={() => setShowConfigModal(false)}>
                      <span>&times;</span>
                    </button>
                  </div>
                  
                  <div className="px-4 pt-3 bg-light border-bottom">
                    <ul className="nav nav-pills small mb-2" style={{ gap: '5px' }}>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={"nav-link py-1 px-3 font-weight-bold border-0 " + (activeConfigTab === 'delete-paths' ? 'active btn-primary' : 'text-secondary bg-transparent')}
                          onClick={() => setActiveConfigTab('delete-paths')}
                          style={{ borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Pliki do usunięcia
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={"nav-link py-1 px-3 font-weight-bold border-0 " + (activeConfigTab === 'update-versions' ? 'active btn-primary' : 'text-secondary bg-transparent')}
                          onClick={() => setActiveConfigTab('update-versions')}
                          style={{ borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Wersje aktualizacji
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="modal-body p-4">
                    {activeConfigTab === 'delete-paths' ? (
                      <>
                        <p className="small text-muted mb-3">
                          Lista ścieżek plików/folderów na maszynach zdalnych do sprawdzania.
                        </p>

                        <div className="form-group mb-3">
                          <div className="d-flex" style={{ gap: '10px' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm text-monospace"
                              style={{ fontSize: '12px' }}
                              value={newFilePathInput}
                              onChange={(e) => setNewFilePathInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddNewFilePath();
                                }
                              }}
                              placeholder="np. /home/pi/test.log"
                            />
                            <button
                              type="button"
                              className="btn btn-success btn-sm font-weight-bold px-3 rounded shadow-sm d-flex align-items-center"
                              onClick={handleAddNewFilePath}
                            >
                              <i className="fas fa-plus mr-1" /> Dodaj
                            </button>
                          </div>
                        </div>

                        <div className="border rounded bg-light p-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          {tempFilesList.length === 0 ? (
                            <div className="text-muted small text-center py-3">Brak skonfigurowanych ścieżek. Dodaj pierwszą ścieżkę powyżej.</div>
                          ) : (
                            <div className="list-group list-group-flush">
                              {tempFilesList.map((pathItem, idx) => (
                                <div key={idx} className="list-group-item d-flex justify-content-between align-items-center px-2 bg-transparent border-bottom small" style={{ borderColor: '#e9ecef', paddingTop: '6px', paddingBottom: '6px' }}>
                                  <span className="text-monospace text-truncate font-weight-bold mr-2 text-dark" style={{ fontSize: '11px' }} title={pathItem}>
                                    {pathItem}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm p-1 rounded"
                                    style={{ border: '0', background: 'transparent' }}
                                    onClick={() => {
                                      setTempFilesList(function (prev) {
                                        return prev.filter(function (p) { return p !== pathItem; });
                                      });
                                    }}
                                    title="Usuń z listy"
                                  >
                                    <i className="fas fa-trash-alt text-danger" style={{ fontSize: '12px' }} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="row">
                        <div className="col-md-5 border-right pr-3 mb-3 mb-md-0">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="font-weight-bold text-dark small m-0">Zdefiniowane profile:</h6>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary py-0 px-2 small"
                              style={{ fontSize: '10px' }}
                              onClick={() => {
                                setEditingVersionIndex(null);
                                setVerFormName('');
                                setVerFormSteps([]);
                                setVerFormRollbackSteps([]);
                              }}
                            >
                              <i className="fas fa-plus mr-1" /> Nowy
                            </button>
                          </div>
                          <div className="border rounded bg-light p-2" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                            {tempUpdateVersionsList.length === 0 ? (
                              <div className="text-muted small text-center py-3">Brak zdefiniowanych profili. Dodaj nowy klikając przycisk powyżej.</div>
                            ) : (
                              <div className="list-group list-group-flush">
                                {tempUpdateVersionsList.map((ver, idx) => (
                                  <div 
                                    key={idx} 
                                    className={`list-group-item d-flex justify-content-between align-items-center px-2 border-bottom small ${editingVersionIndex === idx ? 'bg-info text-white' : 'bg-transparent'}`} 
                                    style={{ borderColor: '#e9ecef', paddingTop: '6px', paddingBottom: '6px', cursor: 'pointer' }}
                                    onClick={() => handleEditVersionClick(idx)}
                                  >
                                    <span className={`font-weight-bold text-truncate ${editingVersionIndex === idx ? 'text-white' : 'text-dark'}`} style={{ fontSize: '11px' }}>
                                      {ver.name}
                                    </span>
                                    <div className="d-flex align-items-center">
                                      <button
                                        type="button"
                                        className="btn btn-sm p-0 ml-2"
                                        style={{ color: editingVersionIndex === idx ? '#fff' : '#dc3545' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteVersionClick(idx);
                                        }}
                                        title="Usuń profil"
                                      >
                                        <i className="fas fa-trash-alt" style={{ fontSize: '10px' }} />
                                      </button>
                                      <i className={`fas fa-chevron-right ml-2 ${editingVersionIndex === idx ? 'text-white' : 'text-muted'}`} style={{ fontSize: '10px' }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-md-7 pl-md-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h6 className="font-weight-bold text-dark small m-0">
                              {editingVersionIndex !== null ? 'Edycja profilu' : 'Nowy profil'}:
                            </h6>
                            <button
                              type="button"
                              className="btn btn-success btn-sm py-0 px-2 small"
                              onClick={handleSaveVersionForm}
                            >
                              <i className="fas fa-save mr-1" /> Zapisz
                            </button>
                          </div>
                          
                          <div className="bg-light border rounded p-2" style={{ fontSize: '11px', maxHeight: '330px', overflowY: 'auto' }}>
                            <div className="form-group mb-2">
                              <label className="mb-0 font-weight-bold text-secondary">Nazwa wersji <span className="text-danger">*</span></label>
                              <input type="text" className="form-control form-control-sm" style={{ fontSize: '11px' }} value={verFormName} onChange={e => setVerFormName(e.target.value)} placeholder="np. Wersja 2.0.1" />
                            </div>
                            
                            <div className="mt-3 pt-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="mb-0 text-secondary font-weight-bold">Kroki Aktualizacji</label>
                                <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2" onClick={() => handleAddStep('step')}>
                                  <i className="fas fa-plus mr-1" /> Dodaj Krok
                                </button>
                              </div>
                              {verFormSteps.length === 0 ? (
                                <div className="text-muted small" style={{ fontSize: '10px' }}>Brak zdefiniowanych kroków. Wersja bezkrokowa będzie bezużyteczna, dodaj krok.</div>
                              ) : (
                                verFormSteps.map((step, idx) => (
                                  <div key={idx} className="border rounded p-2 mb-2 bg-white">
                                    <div className="d-flex justify-content-between mb-1">
                                      <span className="small font-weight-bold">Krok {idx + 1}</span>
                                      <div>
                                        <button type="button" className="btn btn-sm btn-light p-0 px-1 mx-1" disabled={idx === 0} onClick={() => handleMoveStep('step', idx, -1)}><i className="fas fa-arrow-up" /></button>
                                        <button type="button" className="btn btn-sm btn-light p-0 px-1 mx-1" disabled={idx === verFormSteps.length - 1} onClick={() => handleMoveStep('step', idx, 1)}><i className="fas fa-arrow-down" /></button>
                                        <button type="button" className="btn btn-sm text-danger p-0 px-1 mx-1" onClick={() => handleRemoveStep('step', idx)}><i className="fas fa-times" /></button>
                                      </div>
                                    </div>
                                    <input type="text" className="form-control form-control-sm mb-1" style={{ fontSize: '11px' }} placeholder="Nazwa kroku (np. Pobieranie API)" value={step.name} onChange={e => handleUpdateStep('step', idx, 'name', e.target.value)} />
                                    <textarea className="form-control form-control-sm" rows="3" style={{ fontFamily: 'monospace', fontSize: '10px' }} placeholder="Skrypt bash (np. wget URL && tar -xzf ...)" value={step.command} onChange={e => handleUpdateStep('step', idx, 'command', e.target.value)} />
                                  </div>
                                ))
                              )}
                            </div>

                            <div className="mt-3 border-top pt-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="mb-0 text-secondary font-weight-bold">Kroki Wycofywania (Rollback)</label>
                                <button type="button" className="btn btn-sm btn-outline-warning py-0 px-2" onClick={() => handleAddStep('rollback')}>
                                  <i className="fas fa-plus mr-1" /> Dodaj Krok
                                </button>
                              </div>
                              {verFormRollbackSteps.length === 0 ? (
                                <div className="text-muted small" style={{ fontSize: '10px' }}>Brak zdefiniowanych kroków rollbacku. W przypadku błędu aktualizacji, nic nie zostanie przywrócone.</div>
                              ) : (
                                verFormRollbackSteps.map((step, idx) => (
                                  <div key={idx} className="border rounded p-2 mb-2 bg-white">
                                    <div className="d-flex justify-content-between mb-1">
                                      <span className="small font-weight-bold">Krok Rollback {idx + 1}</span>
                                      <div>
                                        <button type="button" className="btn btn-sm btn-light p-0 px-1 mx-1" disabled={idx === 0} onClick={() => handleMoveStep('rollback', idx, -1)}><i className="fas fa-arrow-up" /></button>
                                        <button type="button" className="btn btn-sm btn-light p-0 px-1 mx-1" disabled={idx === verFormRollbackSteps.length - 1} onClick={() => handleMoveStep('rollback', idx, 1)}><i className="fas fa-arrow-down" /></button>
                                        <button type="button" className="btn btn-sm text-danger p-0 px-1 mx-1" onClick={() => handleRemoveStep('rollback', idx)}><i className="fas fa-times" /></button>
                                      </div>
                                    </div>
                                    <input type="text" className="form-control form-control-sm mb-1" style={{ fontSize: '11px' }} placeholder="Nazwa kroku (np. Przywracanie API)" value={step.name} onChange={e => handleUpdateStep('rollback', idx, 'name', e.target.value)} />
                                    <textarea className="form-control form-control-sm" rows="3" style={{ fontFamily: 'monospace', fontSize: '10px' }} placeholder="Skrypt bash (np. mv api_bkp vendim-rest-api)" value={step.command} onChange={e => handleUpdateStep('rollback', idx, 'command', e.target.value)} />
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer border-0 bg-light">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm rounded shadow-sm"
                      onClick={() => setShowConfigModal(false)}
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm rounded shadow-sm px-3"
                      onClick={handleSaveConfig}
                    >
                      Zapisz konfigurację na serwerze
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showUpdateModal && (
            (() => {
              const selectedVersionData = updateVersionsList.filter(function (v) {
                return v.name === selectedUpdateVersionName;
              })[0] || null;

              return (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
                  <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content border-0 shadow-lg">
                      <div className="modal-header bg-light border-0">
                        <h5 className="modal-title font-weight-bold">
                          <i className="fas fa-upload mr-2 text-info" />
                          Aktualizacja maszyny
                        </h5>
                        <button type="button" className="close" onClick={() => setShowUpdateModal(false)}>
                          <span>&times;</span>
                        </button>
                      </div>
                      <div className="modal-body p-4">
                        <div className="form-group mb-3">
                          <label className="small font-weight-bold text-dark mb-1">Wybierz wersję aktualizacji:</label>
                          <select 
                            className="form-control form-control-sm text-dark font-weight-bold"
                            value={selectedUpdateVersionName}
                            onChange={(e) => setSelectedUpdateVersionName(e.target.value)}
                          >
                            <option value="">-- Wybierz wersję --</option>
                            {updateVersionsList.map((ver, idx) => (
                              <option key={idx} value={ver.name}>{ver.name}</option>
                            ))}
                          </select>
                        </div>



                        {updateSteps.length > 0 && (
                          <div className="mt-3 border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="small font-weight-bold text-dark">
                                <i className="fas fa-tasks mr-1 text-info" /> Postęp aktualizacji:
                              </div>
                              <span className={`badge ${updateError ? 'badge-danger' : updateProgressPct === 100 ? 'badge-success' : 'badge-info'} px-2 py-1`} style={{ fontSize: '11px' }}>
                                {updateProgressPct}%
                              </span>
                            </div>
                            {updateSteps.map(function (s) {
                              return (
                                <div key={s.step} className="d-flex align-items-center mb-1 small">
                                  <span className="mr-2" style={{ width: '20px', textAlign: 'center' }}>
                                    {s.status === 'ok' ? '✅' : s.status === 'error' ? '❌' : s.status === 'skip' ? '⏭️' : s.status === 'running' ? '🔄' : '⏳'}
                                  </span>
                                  <span className={
                                    s.status === 'ok' ? 'text-success font-weight-bold' :
                                    s.status === 'error' ? 'text-danger font-weight-bold' :
                                    s.status === 'skip' ? 'text-muted' :
                                    'text-secondary'
                                  }>
                                    Krok {s.step}: {s.name}
                                    {s.status === 'skip' && ' (pominięto)'}
                                  </span>
                                </div>
                              );
                            })}

                            <div className="mt-3">
                              <div className="progress" style={{ height: '14px', borderRadius: '7px', backgroundColor: '#e9ecef' }}>
                                <div
                                  className={`progress-bar progress-bar-striped ${isUpdating ? 'progress-bar-animated' : ''} ${
                                    updateError ? 'bg-danger' : updateProgressPct === 100 ? 'bg-success' : 'bg-info'
                                  }`}
                                  role="progressbar"
                                  style={{
                                    width: `${updateProgressPct}%`,
                                    transition: 'width 0.4s ease-in-out',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    lineHeight: '14px'
                                  }}
                                >
                                  {updateProgressPct >= 12 ? `${updateProgressPct}%` : ''}
                                </div>
                              </div>
                              {isUpdating && (
                                <div className="text-center text-muted mt-1" style={{ fontSize: '10px' }}>
                                  Aktualizacja w toku ({updateProgressPct}%) — proszę czekać...
                                </div>
                              )}
                            </div>

                            {lastExecutedCommand && (
                              <div className="mt-2 p-2 rounded border bg-white shadow-xs">
                                <div className="text-muted font-weight-bold mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  <i className="fas fa-terminal mr-1 text-secondary" />
                                  {isUpdating ? 'Aktualnie wykonywana komenda:' : 'Ostatnio wywołana komenda:'}
                                </div>
                                <div
                                  className="text-dark"
                                  style={{
                                    fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                    fontSize: '10.5px',
                                    wordBreak: 'break-all',
                                    whiteSpace: 'pre-wrap',
                                    backgroundColor: '#f8f9fa',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #e9ecef',
                                    maxHeight: '70px',
                                    overflowY: 'auto'
                                  }}
                                >
                                  {lastExecutedCommand}
                                </div>
                              </div>
                            )}

                            {updateLog && (
                              <details className="mt-2">
                                <summary className="small font-weight-bold text-secondary cursor-pointer" style={{ cursor: 'pointer' }}>Pokaż szczegółowe logi</summary>
                                <pre className="mt-1 bg-dark text-light rounded p-2 mb-0" style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                  {updateLog}
                                </pre>
                              </details>
                            )}
                            {updateError && (
                              <div className="alert alert-danger py-1 px-2 mt-2 mb-0 small font-weight-bold">
                                <i className="fas fa-exclamation-triangle mr-1" />
                                {updateError}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="modal-footer border-0 bg-light">
                        {!isUpdating && selectedUpdateVersionName && selectedNode && (
                          <button
                            type="button"
                            className={`btn ${updateError ? 'btn-danger' : 'btn-outline-danger'} btn-sm rounded shadow-sm font-weight-bold`}
                            onClick={handleRollbackUpdate}
                          >
                            <i className="fas fa-undo mr-1" /> Rollback
                          </button>
                        )}
                        {selectedUpdateVersionName && selectedNode && !isUpdating && (
                          <button
                            type="button"
                            className="btn btn-warning btn-sm rounded shadow-sm font-weight-bold"
                            onClick={handleExecuteUpdate}
                          >
                            <i className="fas fa-upload mr-1" /> Aktualizuj maszynę
                          </button>
                        )}
                        {isUpdating && (
                          <button type="button" className="btn btn-warning btn-sm rounded shadow-sm font-weight-bold" disabled>
                            <i className="fas fa-spinner fa-spin mr-1" /> Aktualizacja w toku...
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm rounded shadow-sm"
                          onClick={() => setShowUpdateModal(false)}
                          disabled={isUpdating}
                        >
                          Zamknij
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          )}

          {showCustomCmdModal && selectedNode && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
              <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '880px' }}>
                <div className="modal-content border-0 shadow-lg">
                  
                  {/* Modal Header */}
                  <div className="modal-header bg-dark text-white border-0 py-3">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-terminal text-warning mr-2" style={{ fontSize: '18px' }} />
                      <h5 className="modal-title font-weight-bold text-white mb-0" style={{ fontSize: '16px' }}>
                        Wyślij custom komendy do maszyny
                      </h5>
                      <span className="badge badge-warning text-dark font-weight-bold ml-2 px-2 py-1" style={{ fontSize: '11px' }}>
                        {selectedNode.givenName || selectedNode.name}
                      </span>
                      <span className="badge badge-light text-monospace ml-2 px-2 py-1" style={{ fontSize: '10px' }}>
                        pi@{(selectedNode.ipAddresses && selectedNode.ipAddresses[0]) || ''}
                      </span>
                    </div>
                    <button 
                      type="button" 
                      className="close text-white" 
                      onClick={() => !isCustomCmdRunning && setShowCustomCmdModal(false)}
                      disabled={isCustomCmdRunning}
                      style={{ opacity: 0.8 }}
                    >
                      <span>&times;</span>
                    </button>
                  </div>

                  <div className="modal-body p-4" style={{ backgroundColor: '#fcfcfd' }}>
                    {/* Presets & Templates Toolbar */}
                    <div className="card border mb-3 shadow-xs" style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0' }}>
                      <div className="card-body p-2 d-flex flex-wrap align-items-center justify-content-between" style={{ gap: '8px' }}>
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
                          <span className="text-secondary small font-weight-bold mr-1">
                            <i className="fas fa-magic text-warning mr-1" /> Szablony:
                          </span>
                          <div className="dropdown d-inline-block">
                            <select
                              className="form-control form-control-sm text-dark font-weight-bold"
                              style={{ width: 'auto', fontSize: '11px', height: '28px', minWidth: '200px' }}
                              defaultValue=""
                              onChange={(e) => {
                                const val = e.target.value;
                                if (!val) return;
                                const allP = [...builtInPresets, ...customPresets];
                                const found = allP.find(p => p.name === val);
                                if (found) handleApplyPreset(found);
                                e.target.value = "";
                              }}
                            >
                              <option value="" disabled>-- Wybierz gotowy szablon --</option>
                              <optgroup label="Wbudowane szablony">
                                {builtInPresets.map((p, idx) => (
                                  <option key={'b_' + idx} value={p.name}>{p.name}</option>
                                ))}
                              </optgroup>
                              {customPresets.length > 0 && (
                                <optgroup label="Własne szablony (zapisane)">
                                  {customPresets.map((p, idx) => (
                                    <option key={'c_' + idx} value={p.name}>{p.name}</option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm px-2 py-0"
                            style={{ fontSize: '11px', height: '28px' }}
                            onClick={() => setShowSavePresetInput(!showSavePresetInput)}
                            title="Zapisz bieżącą konfigurację komend jako szablon"
                          >
                            <i className="fas fa-bookmark mr-1 text-primary" /> Zapisz szablon
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm px-2 py-0"
                            style={{ fontSize: '11px', height: '28px' }}
                            onClick={handleClearCustomCommands}
                            title="Wyczyść listę komend"
                            disabled={isCustomCmdRunning}
                          >
                            <i className="fas fa-eraser mr-1" /> Wyczyść
                          </button>
                        </div>

                        {customPresets.length > 0 && (
                          <div className="d-flex align-items-center">
                            <span className="badge badge-light text-secondary border mr-1" style={{ fontSize: '10px' }}>
                              Własne: {customPresets.length}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Save Preset Input Bar */}
                      {showSavePresetInput && (
                        <div className="p-2 border-top bg-light d-flex align-items-center" style={{ gap: '8px' }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Nazwa nowego szablonu np. Szybki restart i status"
                            value={newPresetName}
                            onChange={(e) => setNewPresetName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveCustomPreset();
                              }
                            }}
                            style={{ fontSize: '11px', maxWidth: '350px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-primary btn-sm font-weight-bold px-3"
                            style={{ fontSize: '11px', height: '28px' }}
                            onClick={handleSaveCustomPreset}
                          >
                            <i className="fas fa-save mr-1" /> Zapisz
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            style={{ fontSize: '11px', height: '28px' }}
                            onClick={() => setShowSavePresetInput(false)}
                          >
                            Anuluj
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Commands List Card */}
                    <div className="card border shadow-sm mb-3 bg-white">
                      <div className="card-header bg-light py-2 px-3 d-flex justify-content-between align-items-center">
                        <div className="font-weight-bold text-dark small">
                          <i className="fas fa-list-ol text-primary mr-1" /> Sekwencja komend ({customCommandsList.length})
                        </div>
                      </div>
                      
                      <div className="card-body p-3">
                        <div className="d-flex flex-column" style={{ gap: '10px' }}>
                          {customCommandsList.map((cmdItem, idx) => (
                            <div 
                              key={cmdItem.id || idx} 
                              className="p-2 rounded border transition-all"
                              style={{ 
                                backgroundColor: cmdItem.showResponse ? '#f0f7ff' : '#f8f9fa',
                                borderColor: cmdItem.showResponse ? '#b8daff' : '#e9ecef',
                                borderWidth: '1px'
                              }}
                            >
                              <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                {/* Index Badge */}
                                <div 
                                  className="badge badge-dark font-weight-bold d-flex align-items-center justify-content-center"
                                  style={{ width: '28px', height: '28px', borderRadius: '6px', fontSize: '11px', flexShrink: 0 }}
                                >
                                  #{idx + 1}
                                </div>

                                {/* Command Input Field */}
                                <div className="flex-grow-1" style={{ minWidth: '220px' }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm text-dark font-weight-bold text-monospace"
                                    style={{ 
                                      fontSize: '11.5px', 
                                      backgroundColor: '#ffffff',
                                      borderColor: cmdItem.showResponse ? '#9ac7fa' : '#ced4da',
                                      fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                                    }}
                                    placeholder={`np. sudo systemctl restart multivend`}
                                    value={cmdItem.command}
                                    onChange={(e) => handleUpdateCustomCommand(idx, 'command', e.target.value)}
                                    disabled={isCustomCmdRunning}
                                  />
                                </div>

                                {/* Show Response Checkbox */}
                                <div 
                                  className={`d-flex align-items-center px-2 py-1 rounded cursor-pointer border ${cmdItem.showResponse ? 'bg-primary text-white border-primary' : 'bg-white text-secondary border-light'}`}
                                  style={{ cursor: 'pointer', flexShrink: 0, userSelect: 'none', height: '30px' }}
                                  onClick={() => handleUpdateCustomCommand(idx, 'showResponse', !cmdItem.showResponse)}
                                  title="Zaznacz, jeśli chcesz zobaczyć odpowiedź/wynik z tej komendy w konsoli"
                                >
                                  <input
                                    type="checkbox"
                                    className="cursor-pointer mr-1"
                                    checked={cmdItem.showResponse}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleUpdateCustomCommand(idx, 'showResponse', e.target.checked);
                                    }}
                                    disabled={isCustomCmdRunning}
                                    style={{ width: '14px', height: '14px' }}
                                  />
                                  <span className="small font-weight-bold" style={{ fontSize: '11px' }}>
                                    <i className={`fas ${cmdItem.showResponse ? 'fa-eye' : 'fa-eye-slash'} mr-1`} />
                                    Pokaż wynik
                                  </span>
                                </div>

                                {/* Reorder & Remove Buttons */}
                                <div className="d-flex align-items-center" style={{ gap: '3px', flexShrink: 0 }}>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm p-1"
                                    style={{ width: '26px', height: '28px', border: '1px solid #ced4da' }}
                                    onClick={() => handleMoveCustomCommand(idx, -1)}
                                    disabled={idx === 0 || isCustomCmdRunning}
                                    title="Przesuń w górę"
                                  >
                                    <i className="fas fa-arrow-up" style={{ fontSize: '10px' }} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm p-1"
                                    style={{ width: '26px', height: '28px', border: '1px solid #ced4da' }}
                                    onClick={() => handleMoveCustomCommand(idx, 1)}
                                    disabled={idx === customCommandsList.length - 1 || isCustomCmdRunning}
                                    title="Przesuń w dół"
                                  >
                                    <i className="fas fa-arrow-down" style={{ fontSize: '10px' }} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm p-1 ml-1"
                                    style={{ width: '26px', height: '28px' }}
                                    onClick={() => handleRemoveCustomCommand(idx)}
                                    disabled={isCustomCmdRunning}
                                    title="Usuń komendę"
                                  >
                                    <i className="fas fa-trash-alt" style={{ fontSize: '10px' }} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Add command button */}
                        <div className="mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm font-weight-bold px-3 rounded shadow-xs"
                            style={{ fontSize: '11px' }}
                            onClick={handleAddCustomCommand}
                            disabled={isCustomCmdRunning}
                          >
                            <i className="fas fa-plus-circle mr-1" /> Dodaj kolejną komendę
                          </button>
                        </div>
                      </div>

                      {/* Options Toolbar */}
                      <div className="card-footer bg-light p-2 px-3 border-top d-flex flex-wrap align-items-center justify-content-between small" style={{ gap: '10px' }}>
                        <label className="d-flex align-items-center mb-0 cursor-pointer text-dark font-weight-bold" style={{ cursor: 'pointer', userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            checked={customCmdStopOnError}
                            onChange={(e) => setCustomCmdStopOnError(e.target.checked)}
                            className="mr-2"
                            disabled={isCustomCmdRunning}
                          />
                          <span>Stop on error</span>
                        </label>

                        <div className="d-flex align-items-center">
                          <span className="text-secondary mr-2" style={{ fontSize: '11px' }}>Limit czasu:</span>
                          <select
                            className="form-control form-control-sm py-0"
                            style={{ width: '90px', fontSize: '11px', height: '26px' }}
                            value={customCmdTimeout}
                            onChange={(e) => setCustomCmdTimeout(Number(e.target.value))}
                            disabled={isCustomCmdRunning}
                          >
                            <option value={30000}>30 sek</option>
                            <option value={60000}>60 sek</option>
                            <option value={120000}>2 min</option>
                            <option value={180000}>3 min</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Running Indicator */}
                    {isCustomCmdRunning && (
                      <div className="p-4 border rounded bg-white shadow-sm text-center mb-3">
                        <i className="fas fa-spinner fa-spin fa-2x text-warning mb-2" />
                        <h6 className="font-weight-bold text-dark mb-1">Trwa wykonywanie komend przez SSH...</h6>
                        <div className="small text-muted mb-3">
                          Nawiązywanie połączenia z <code>{selectedNode.ipAddresses && selectedNode.ipAddresses[0]}</code> i sekwencyjne przetwarzanie instrukcji.
                        </div>
                        <div className="progress" style={{ height: '8px', borderRadius: '4px', maxWidth: '350px', margin: '0 auto' }}>
                          <div className="progress-bar progress-bar-striped progress-bar-animated bg-warning" style={{ width: '100%' }} />
                        </div>
                      </div>
                    )}

                    {/* General Error Alert */}
                    {customCmdError && !customCmdResults && (
                      <div className="alert alert-danger shadow-sm mb-3 small font-weight-bold d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle fa-lg mr-2" />
                        <div>{customCmdError}</div>
                      </div>
                    )}

                    {/* Results Section */}
                    {customCmdResults && (
                      <div className="card border-0 shadow-sm mb-3">
                        <div 
                          className={`card-header py-2 px-3 text-white font-weight-bold d-flex justify-content-between align-items-center ${
                            customCmdResults.success ? 'bg-success' : 'bg-danger'
                          }`}
                          style={{ borderRadius: '4px 4px 0 0' }}
                        >
                          <div className="d-flex align-items-center">
                            <i className={`fas ${customCmdResults.success ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`} />
                            <span>
                              {customCmdResults.success ? 'Wszystkie komendy wykonane pomyślnie' : 'Wystąpił błąd podczas wykonywania komend'}
                            </span>
                            <span className="badge badge-light text-dark ml-2" style={{ fontSize: '10px' }}>
                              Czas: {((customCmdResults.durationMs || 0) / 1000).toFixed(2)}s
                            </span>
                          </div>
                          <div className="d-flex" style={{ gap: '5px' }}>
                            <button
                              type="button"
                              className="btn btn-light btn-sm font-weight-bold py-0 px-2 shadow-xs"
                              style={{ fontSize: '10px', height: '24px' }}
                              onClick={handleCopyCustomCmdReport}
                              title="Kopiuj pełny raport z wykonania"
                            >
                              <i className="fas fa-copy mr-1 text-dark" /> Kopiuj raport
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-light btn-sm font-weight-bold py-0 px-2"
                              style={{ fontSize: '10px', height: '24px' }}
                              onClick={() => setCustomCmdResults(null)}
                              title="Wyczyść wyniki"
                            >
                              <i className="fas fa-times" />
                            </button>
                          </div>
                        </div>

                        <div className="card-body p-3 bg-white border border-top-0 rounded-bottom">
                          {customCmdResults.error && (
                            <div className="alert alert-danger py-1 px-2 mb-3 small font-weight-bold">
                              <i className="fas fa-exclamation-triangle mr-1" />
                              {customCmdResults.error}
                            </div>
                          )}

                          <div className="d-flex flex-column" style={{ gap: '12px' }}>
                            {customCmdResults.results.map((resItem) => {
                              const isRevealed = revealedResponses[resItem.index];
                              const shouldShowOutput = resItem.showResponse || isRevealed || resItem.status === 'error';

                              return (
                                <div 
                                  key={resItem.index} 
                                  className="border rounded shadow-2xs overflow-hidden"
                                  style={{
                                    borderColor: resItem.status === 'success' ? '#c3e6cb' : resItem.status === 'error' ? '#f5c6cb' : '#e2e8f0'
                                  }}
                                >
                                  {/* Result Item Header */}
                                  <div 
                                    className="p-2 px-3 d-flex flex-wrap justify-content-between align-items-center border-bottom"
                                    style={{
                                      backgroundColor: resItem.status === 'success' ? '#f4fbf6' : resItem.status === 'error' ? '#fdf2f2' : '#f8f9fa'
                                    }}
                                  >
                                    <div className="d-flex align-items-center mr-2">
                                      <span 
                                        className={`badge mr-2 ${
                                          resItem.status === 'success' ? 'badge-success' : resItem.status === 'error' ? 'badge-danger' : 'badge-secondary'
                                        }`}
                                        style={{ fontSize: '10px' }}
                                      >
                                        #{resItem.index} {resItem.status === 'success' ? 'SUKCES' : resItem.status === 'error' ? 'BŁĄD' : 'POMINIĘTO'}
                                      </span>
                                      <code className="text-dark font-weight-bold text-break" style={{ fontSize: '11px' }}>
                                        {resItem.command}
                                      </code>
                                    </div>

                                    <div className="d-flex align-items-center mt-1 mt-md-0" style={{ gap: '6px' }}>
                                      {resItem.exitCode !== null && (
                                        <span className="badge badge-light border text-monospace text-muted" style={{ fontSize: '10px' }}>
                                          exit: {resItem.exitCode}
                                        </span>
                                      )}
                                      {!resItem.showResponse && resItem.status === 'success' && (
                                        <span className="badge badge-secondary" style={{ fontSize: '9px' }}>
                                          odpowiedź ukryta
                                        </span>
                                      )}
                                      {shouldShowOutput && resItem.output && (
                                        <button
                                          type="button"
                                          className="btn btn-outline-dark btn-sm py-0 px-2"
                                          style={{ fontSize: '10px', height: '22px' }}
                                          onClick={() => {
                                            navigator.clipboard.writeText(resItem.output);
                                            NotificationManager.success('Skopiowano wynik komendy!', 'Sukces', 1500);
                                          }}
                                          title="Kopiuj wynik tej komendy"
                                        >
                                          <i className="fas fa-copy mr-1" /> Kopiuj
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {/* Result Item Body (Terminal or Hidden Notice) */}
                                  <div className="p-2 bg-white">
                                    {shouldShowOutput ? (
                                      resItem.output ? (
                                        <pre 
                                          className="p-2 mb-0 rounded text-monospace"
                                          style={{
                                            backgroundColor: '#1e1e24',
                                            color: resItem.status === 'error' ? '#ff7b72' : '#7ee787',
                                            fontSize: '10.5px',
                                            maxHeight: '220px',
                                            overflowY: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                            fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                                          }}
                                        >
                                          {resItem.output}
                                        </pre>
                                      ) : (
                                        <div className="text-muted small py-1 px-2 font-italic" style={{ fontSize: '11px' }}>
                                          (Komenda wykonana pomyślnie, brak wyjścia na konsoli stdout/stderr)
                                        </div>
                                      )
                                    ) : (
                                      <div className="d-flex justify-content-between align-items-center py-1 px-2 bg-light rounded text-muted small" style={{ fontSize: '11px' }}>
                                        <span>
                                          <i className="fas fa-eye-slash mr-1 text-secondary" />
                                          Odpowiedź z tej komendy została ukryta zgodnie z konfiguracją.
                                        </span>
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm p-0 text-primary font-weight-bold"
                                          style={{ fontSize: '11px', textDecoration: 'none' }}
                                          onClick={() => toggleRevealResponse(resItem.index)}
                                        >
                                          Pokaż odpowiedź mimo to &raquo;
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Modal Footer */}
                  <div className="modal-footer bg-light border-top py-2 px-4 d-flex justify-content-between align-items-center">
                    <div className="small text-muted">
                      {isCustomCmdRunning ? (
                        <span><i className="fas fa-spinner fa-spin mr-1 text-warning" /> Wykonywanie instrukcji przez SSH...</span>
                      ) : (
                        <span>Łącznie komend w sekwencji: <strong>{customCommandsList.length}</strong></span>
                      )}
                    </div>

                    <div className="d-flex" style={{ gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm rounded shadow-xs px-3"
                        onClick={() => setShowCustomCmdModal(false)}
                        disabled={isCustomCmdRunning}
                      >
                        Zamknij
                      </button>

                      {isCustomCmdRunning ? (
                        <button type="button" className="btn btn-warning btn-sm font-weight-bold px-3" disabled>
                          <i className="fas fa-spinner fa-spin mr-1" /> Wykonywanie...
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-success btn-sm font-weight-bold px-4 shadow-sm"
                          onClick={handleExecuteCustomCommands}
                        >
                          <i className="fas fa-paper-plane mr-1" /> Wyślij i wykonaj komendy
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default VPN;
